import { Grid, GridConfig } from './core/Grid.js';
import { GridGenerator } from './core/GridGenerator.js';
import { PersistenceManager, VORTEX_TUTORIAL_SIZE, VORTEX_UNLOCK_CHALLENGES } from './persistence/PersistenceManager.js';
import { GameType, GAME_TYPES, INITIAL_TO_GAME, GameOrder, orderedGameTypes, ChallengeRef, SavedGrid, gridGameSet, CLASSIC_GAME_TYPES, CLASSIC_SCOPE, scopeToGameTypes, gameTypesToScope } from './persistence/types.js';
import { ApiClient } from './api/ApiClient.js';
import { AdminApi } from './api/AdminApi.js';
import {
    GameInstance,
    createGameInstance,
    reactivateGameInstance,
    getGameTitle,
} from './games/GameRegistry.js';
import { GridCreator } from './ui/GridCreator.js';
import { GridSelector } from './ui/GridSelector.js';
import { GridDuplicator, DuplicateRequest, DuplicateResult } from './ui/GridDuplicator.js';
import { AdminPanel } from './ui/AdminPanel.js';
import { WelcomePanel, VortexWelcomeContext } from './ui/WelcomePanel.js';
import { SettingsPanel } from './ui/SettingsPanel.js';
import { Leaderboard } from './ui/Leaderboard.js';
import { TutorialHub } from './ui/TutorialHub.js';
import { ModalManager } from './ui/ModalManager.js';
import { SidebarController } from './ui/SidebarController.js';
import { LayoutManager } from './ui/LayoutManager.js';
import { KeyboardController, FinePoint } from './input/KeyboardController.js';
import { KeyboardCursorStyle } from './rendering/GridRenderer.js';
import { TutorialEngine, MetaTutorialId } from './tutorials/TutorialEngine.js';
import { TutorialEntry, getTutorial, getVortexTutorial, getMetaTutorial } from './tutorials/TutorialRegistry.js';

enum VortexMode {
    NORMAL_GAME,     // Hub off, normal play
    IN_GAME_GATES,   // Hub off, Activate Vortex: toggle/create gate pairs in current game
    HUB,             // Hub on, Links mode: create/link/delete gate pairs
}

// Undo-style icon for the Restore buttons, prepended to their (dynamic) labels
const UNDO_ICON = '<svg class="restore-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 14 4 9 9 4"/><path d="M4 9h11a5 5 0 0 1 0 10H9"/></svg>';

// Same star shape as the scoring-rules button (index.html); flanks the endgame
// "Total score" banner in the status line.
const SCORE_STAR_PATH = 'M12 2 L15.1 7.8 L21.5 8.9 L17 13.6 L17.9 20.1 L12 17.2 L6.1 20.1 L7 13.6 L2.5 8.9 L8.9 7.8 Z';

// Win flourish (styles.css). Every class each animation can carry, so a run can
// clear whatever the last one left on before starting.
const CANVAS_FLASH_CLASSES = ['win-flash', 'win-flash-vortex'];
const STAR_FLASH_CLASSES = ['star-flash', 'star-flash-delayed'];

/** What a board change earned, if anything — see `playWinFlourish`. */
interface WinFlourish {
    /** No best was stored before, so the score button has just appeared. */
    firstWin: boolean;
    /** This state became the new best, first one included. */
    improved: boolean;
    /** Every game on the grid is now won. */
    gridComplete: boolean;
}
import { VortexHub } from './vortex/VortexHub.js';
import { VortexHubRenderer } from './vortex/VortexHubRenderer.js';
import { VortexHubInput, PendingDelete } from './vortex/VortexHubInput.js';
import { VortexActivateInput, ActivateRenderer } from './vortex/VortexActivateInput.js';
import { getGatePairUsage, isBestStateCompatible, hasCompatibleGates, GatePairUsage } from './vortex/VortexGateUsage.js';
import { VortexBackupManager } from './vortex/VortexBackupManager.js';
import { parsePositionKey, Position } from './core/GridGeometry.js';
import { deserializeGridData } from './core/GridSerialization.js';
import { isVortexGame, isGateModeReceiver } from './games/IVortexGame.js';
import { formatScoringRule } from './games/scoringRules.js';

class App {
    private generator: GridGenerator;
    private persistence: PersistenceManager;
    private currentGrid: Grid | null = null;
    private currentGridId: string | null = null;
    private currentGameType: GameType = 'mirrors';
    private gameInstance: GameInstance | null = null;
    private gameInstances: Map<GameType, GameInstance> = new Map();
    /** Tracks which center-column overlay (if any) is open; see ModalManager. */
    private modals: ModalManager;
    /** Owns the sidebar icon-button DOM refs (incl. the IconButtons); see SidebarController. */
    private sidebar: SidebarController;
    /** Shared keyboard-play controller: crosshair cursor + keyboard placement. */
    private keyboard: KeyboardController;
    /** Responsive view mode + canvas sizing; see LayoutManager. */
    private layout: LayoutManager;

    private gridCreator: GridCreator;
    private gridDuplicator: GridDuplicator;
    /** What the open duplicate modal will act on when confirmed. */
    private duplicateTarget:
        | { kind: 'grid'; gridId: string }
        | { kind: 'import'; gridData: string; gridConfig: GridConfig }
        | null = null;
    private gridSelector: GridSelector;
    private tutorialHub: TutorialHub;
    private tutorialEngine: TutorialEngine | null = null;
    private preTutorialGridId: string | null = null;
    private activeTutorialTitle: string | null = null;
    /** True while the active tutorial is a Vortex one (themes the progress dots). */
    private tutorialIsVortex = false;

    // Vortex Hub state
    private vortexResetPending: boolean = false;
    private vortexMode: VortexMode = VortexMode.NORMAL_GAME;
    private vortexHub: VortexHub | null = null;
    private vortexHubRenderer: VortexHubRenderer | null = null;
    private vortexHubInput: VortexHubInput | null = null;
    private vortexActivateInput: VortexActivateInput | null = null;
    /** Vortex win/Hub backup snapshotting + scoring; see VortexBackupManager. */
    private vortexBackup: VortexBackupManager;

    private canvas: HTMLCanvasElement;
    private gridSelectorEl: HTMLElement;
    private adminPanelEl: HTMLElement;
    private adminPanel: AdminPanel | null = null;
    private adminVisible: boolean = false;
    private restoreBackupPending: boolean = false;
    private welcomePanelEl: HTMLElement;
    private settingsPanelEl: HTMLElement;
    private welcomePanel: WelcomePanel;
    private settingsPanel: SettingsPanel;
    private welcomeIsVortex: boolean = false;  // which welcome variant is showing (drives the title)
    private leaderboardPanelEl: HTMLElement;
    private leaderboardPanel: Leaderboard;
    private statusEl: HTMLElement;
    private gameTitleEl: HTMLElement;
    private gameScoreEl: HTMLElement;
    private scoreStarRowEl: HTMLElement;
    private copiedMessageEl: HTMLElement;
    private gameInfoEl: HTMLElement;
    private restoreButtonsEl: HTMLElement;
    private gameSelectorEl: HTMLElement;
    private gameButtons: NodeListOf<HTMLButtonElement>;
    private gameHoverInfoEl: HTMLElement;
    private tutorialHintEl: HTMLElement;
    private scoringViewActive: boolean = false;
    /** Live board state saved while previewing the recorded best in the overlay. */
    private scoringPreviewRestore: string | null = null;
    /** Delete Gates button confirm state: the pair pending deletion, or null. */
    private vortexDeletePendingInfo: PendingDelete | null = null;
    /** Delete Gates armed: button clicked with no pair selected; next gate click deletes. */
    private vortexDeleteArmed: boolean = false;

    constructor() {
        this.sidebar = new SidebarController();
        this.canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
        this.statusEl = document.getElementById('status') as HTMLElement;
        this.gameTitleEl = document.getElementById('game-title') as HTMLElement;
        this.gameScoreEl = document.getElementById('game-score') as HTMLElement;
        this.scoreStarRowEl = document.getElementById('score-star-row') as HTMLElement;
        this.copiedMessageEl = document.getElementById('copied-message') as HTMLElement;
        this.gameInfoEl = document.getElementById('game-info') as HTMLElement;
        this.restoreButtonsEl = document.getElementById('restore-buttons') as HTMLElement;
        this.gameSelectorEl = document.getElementById('game-selector') as HTMLElement;
        this.gridSelectorEl = document.getElementById('grid-selector') as HTMLElement;
        this.adminPanelEl = document.getElementById('admin-panel') as HTMLElement;
        this.gameButtons = document.querySelectorAll('.game-btn') as NodeListOf<HTMLButtonElement>;
        this.gameHoverInfoEl = document.getElementById('game-hover-info') as HTMLElement;
        this.tutorialHintEl = document.getElementById('tutorial-hint') as HTMLElement;
        this.welcomePanelEl = document.getElementById('welcome-panel') as HTMLElement;
        this.settingsPanelEl = document.getElementById('settings-panel') as HTMLElement;
        this.leaderboardPanelEl = document.getElementById('leaderboard-panel') as HTMLElement;

        this.layout = new LayoutManager({
            canvas: this.canvas,
            gridSelectorEl: this.gridSelectorEl,
            isCanvasHidden: () => this.modals.inModal,
            onResize: () => {
                if (this.tutorialEngine?.isActive()) this.tutorialEngine.render();
            },
        });

        this.generator = new GridGenerator();
        this.persistence = new PersistenceManager();
        this.vortexBackup = new VortexBackupManager({
            persistence: this.persistence,
            getGridId: () => this.currentGridId,
            getGrid: () => this.currentGrid,
            getHub: () => this.vortexHub,
        });

        // Initialize UI components
        this.gridCreator = new GridCreator(this.gridSelectorEl, {
            onGridCreated: (grid, gameSet) => this.handleGridCreated(grid, gameSet),
            onClose: () => this.onGridCreatorClosed(),
            isVortexUnlocked: (config) => this.persistence.isVortexUnlocked(config),
        });

        this.gridSelector = new GridSelector(this.gridSelectorEl, this.persistence, {
            onGridSelected: (gridId) => this.handleGridSelected(gridId),
            onClose: () => this.onGridSelectorClosed(),
            onCreateNew: () => this.handleCreateFromSelector(),
            onChallengeSelected: (challenge) => this.handleChallengeSelected(challenge),
            showMessage: (msg) => this.showMessage(msg),
            onDuplicate: (g) => this.showGridDuplicator(g),
            onImportGrid: (gridData, gridConfig, gameSet) => this.showImportDuplicator(gridData, gridConfig, gameSet),
            // Authoring instance only — the button stays unrendered in public builds.
            ...(import.meta.env?.VITE_ADMIN ? { onAddCandidate: (g: SavedGrid) => this.handleAddCandidate(g) } : {}),
        });

        this.gridDuplicator = new GridDuplicator(this.gridSelectorEl, this.persistence, {
            onConfirm: (result) => this.handleDuplicateConfirmed(result),
            onClose: () => this.onGridDuplicatorClosed(),
            onSelectGrid: (gridId) => this.handleDuplicateVariantSelected(gridId),
        });

        this.tutorialHub = new TutorialHub(this.gridSelectorEl, {
            onTutorialSelected: (gameType, isVortex) => this.handleTutorialSelected(gameType, isVortex),
            onMetaTutorialSelected: (id) => this.handleMetaTutorialSelected(id),
            onWelcomeSelected: () => this.showWelcome(),
            onVortexWelcomeSelected: () => this.showVortexWelcome(),
            onClose: () => this.onTutorialHubClosed(),
        });

        this.welcomePanel = new WelcomePanel(this.welcomePanelEl, {
            onChooseOrder: (order) => this.chooseGameOrder(order, true),
            onVortexDismiss: () => this.hideWelcome(),
        });

        this.settingsPanel = new SettingsPanel(this.settingsPanelEl, {
            getGameOrder: () => this.persistence.getGameOrder(),
            onChangeGameOrder: (order) => this.chooseGameOrder(order, false),
            onShowWelcome: () => this.showWelcome(),
            onShowVortexWelcome: () => this.showVortexWelcome(),
            isVortexGrid: () => this.isCurrentGridVortex(),
            getUsername: () => ApiClient.getUsername(),
            onSetUsername: (name) => ApiClient.setUsername(name), // saving a name never submits
            getMirrorsHintBeam: () => this.persistence.getMirrorsHintBeam(),
            onSetMirrorsHintBeam: (on) => { this.persistence.setMirrorsHintBeam(on); this.render(); },
            getBranchesHintRings: () => this.persistence.getBranchesHintRings(),
            onSetBranchesHintRings: (on) => { this.persistence.setBranchesHintRings(on); this.render(); },
            getLineHints: () => this.persistence.getLineHints(),
            onSetLineHints: (on) => { this.persistence.setLineHints(on); this.render(); },
            getKeyboardMode: () => this.persistence.getKeyboardMode(),
            onSetKeyboardMode: (on) => { this.persistence.setKeyboardMode(on); this.keyboard.setEnabled(on); },
        });

        // Shared keyboard-play controller (crosshair cursor + placement). One
        // instance for the app; the active game's renderer draws the cursor and
        // its input handles placement. Initial on/off applied after the grid loads.
        this.keyboard = new KeyboardController({
            isInGame: () => this.isKeyboardActive(),
            getGridSize: () => this.currentGrid?.size ?? 10,
            getActiveInput: () => this.activeKeyboardInput(),
            onChange: () => this.render(),
            onToggle: (on) => {
                this.persistence.setKeyboardMode(on);
                // Reflect the new state if the settings panel is open.
                if (this.modals.isOpen('settings')) this.settingsPanel.show();
            },
            onBoardChange: () => this.onGameUpdate(),
            onRestoreBest: () => this.handleRestoreBest(),
            onNextGame: () => {
                const next = this.getNextUnsolvedGame();
                if (next) this.switchGame(next);
            },
            onReset: () => this.resetGame(),
            onToggleGateMode: () => this.toggleGateModeFromKeyboard(),
            onToggleHub: () => this.toggleHubFromKeyboard(),
        });

        this.leaderboardPanel = new Leaderboard(this.leaderboardPanelEl, {
            getSubmitMode: () => this.persistence.getSubmitMode(),
            setSubmitMode: (mode) => this.persistence.setSubmitMode(mode),
            onSubmitNow: () => this.submitCurrentChallenge(),
        });

        // Single source of truth for the open center-column overlay. closeAll() hides
        // whichever modal is open via these per-modal hide callbacks.
        this.modals = new ModalManager({
            gridSelector: () => this.gridSelector.hide(),
            gridCreator: () => this.gridCreator.hide(),
            gridDuplicator: () => this.gridDuplicator.hide(),
            tutorialHub: () => this.tutorialHub.hide(),
            welcome: () => this.welcomePanel.hide(),
            settings: () => this.settingsPanel.hide(),
            leaderboard: () => this.leaderboardPanel.hide(),
        });

        // Admin panel: build-gated behind VITE_ADMIN (local authoring instance only).
        if (import.meta.env?.VITE_ADMIN) {
            this.adminPanel = new AdminPanel(this.adminPanelEl, {
                onClose: () => this.hideAdmin(),
                onPlay: (gridData) => this.handleAdminPlay(gridData),
                onCaptureGyrodiot: (gridData) => this.captureGyrodiotPlay(gridData),
                onLocalPlayState: (gridData) => this.localPlayState(gridData),
                onAuthoringStateLoaded: (state) => {
                    this.persistence.reconcileAuthoringState(state);
                    this.gridSelector.refresh();
                },
            });
            this.sidebar.adminBtn.classList.remove('hidden');
        }

        this.setupButtons();
        this.layout.start();
        this.applyGameOrder();
        this.loadSavedOrDefaultGrid();
        // Apply the persisted keyboard-mode preference now the grid (and its
        // renderer) exist, so the crosshair shows on load when it was left on.
        this.keyboard.setEnabled(this.persistence.getKeyboardMode());

        // Greet genuinely-new players with the intro panel (their localStorage key
        // was absent at load — not just a version mismatch on an existing player).
        if (this.persistence.isFreshInstall()) {
            this.showWelcome();
        }

        // Fetch the active challenge in the background (best-effort; offline-safe).
        void this.initChallenges();
    }

    private toggleAdmin(): void {
        if (!this.adminPanel) return;
        if (this.adminVisible) this.hideAdmin();
        else this.showAdmin();
    }

    private showAdmin(): void {
        if (!this.adminPanel) return;
        // The admin overlay shares the center column with the modals, so it has to take
        // the slot rather than stack on top of one. A modal left open underneath keeps
        // both its pre-admin render and its canvas-hiding UI state, so Play would drop
        // you back onto a stale selector (old grid still pinned as "Now playing", the
        // just-created one missing) with the canvas still hidden behind it.
        this.closeCenterModals();
        this.applyUIState();
        this.adminVisible = true;
        this.sidebar.adminBtn.classList.add('active');
        document.body.classList.add('admin-mode'); // hides all sidebar buttons but Back
        this.adminPanel.show();
    }

    private hideAdmin(): void {
        if (!this.adminPanel) return;
        this.adminVisible = false;
        this.sidebar.adminBtn.classList.remove('active');
        document.body.classList.remove('admin-mode');
        this.adminPanel.hide();
        // A modal may have been open when admin took the slot, in which case its normal
        // close path (which re-picks the view mode) never ran. The canvas is measurable
        // again now, so re-pick it here.
        this.layout.resize();
    }

    // ── Settings & Welcome panels ─────────────────────────────────

    /** Reorder the right-sidebar game-button wrappers to match the order preference. */
    private applyGameOrder(): void {
        const order = orderedGameTypes(this.persistence.getGameOrder());
        for (const gameType of order) {
            const btn = this.gameSelectorEl.querySelector(`.game-btn[data-game="${gameType}"]`);
            const wrapper = btn?.closest('.game-btn-wrapper');
            if (wrapper) this.gameSelectorEl.appendChild(wrapper); // re-append in order
        }
    }

    /** The 12-game set the current grid plays (classic 12 when no grid / no set). */
    private currentGameSet(): Set<GameType> {
        const grid = this.currentGridId ? this.persistence.getGrid(this.currentGridId) : null;
        return new Set(grid ? gridGameSet(grid) : CLASSIC_GAME_TYPES);
    }

    /**
     * Show only the current grid's set in the sidebar (hide the dropped game's button),
     * and, if the active game isn't in this grid's set, move to the first game that is.
     * Called on every grid switch, before the game instance is (re)built.
     */
    private applyGameSet(): void {
        const set = this.currentGameSet();
        this.gameButtons.forEach(btn => {
            const gt = btn.dataset.game as GameType;
            const wrapper = btn.closest('.game-btn-wrapper');
            wrapper?.classList.toggle('hidden', !set.has(gt));
        });

        if (!set.has(this.currentGameType)) {
            const first = orderedGameTypes(this.persistence.getGameOrder()).find(g => set.has(g));
            if (first) {
                this.currentGameType = first;
                this.gameButtons.forEach(btn => btn.classList.toggle('active', btn.dataset.game === first));
                this.gameTitleEl.textContent = getGameTitle(first);
            }
        }
    }

    /** Persist a new game order, re-apply it live, and (from the welcome panel) dismiss. */
    private chooseGameOrder(order: GameOrder, fromWelcome: boolean): void {
        this.persistence.setGameOrder(order);
        this.applyGameOrder();
        if (fromWelcome) this.hideWelcome();
    }

    /**
     * Whether one of the grid modals sharing the center column is open (selector,
     * creator, or duplicator). They all hide the canvas and take over the Grids button,
     * so the surrounding UI treats them alike.
     */
    private get inGridModal(): boolean {
        return this.modals.isOpen('gridSelector')
            || this.modals.isOpen('gridCreator')
            || this.modals.isOpen('gridDuplicator');
    }

    /** Close any open center-column modal so a new one can take over cleanly. */
    private closeCenterModals(): void {
        this.modals.closeAll();
    }

    private toggleLeaderboard(): void {
        if (this.modals.isOpen('leaderboard')) this.hideLeaderboard();
        else this.showLeaderboard();
    }

    private showLeaderboard(): void {
        const ref = this.currentChallengeRef();
        if (!ref || !this.currentGridId) return;
        const grid = this.persistence.getGrid(this.currentGridId);
        this.closeCenterModals();
        this.modals.open('leaderboard');
        void this.leaderboardPanel.show({ id: ref.id, title: grid?.challengeTitle ?? 'Challenge', scope: ref.scope, size: ref.size });
        this.applyUIState();
    }

    private hideLeaderboard(): void {
        this.modals.close('leaderboard');
        this.leaderboardPanel.hide();
        this.applyUIState();
        this.refreshUI();
    }

    private toggleSettings(): void {
        if (this.modals.isOpen('settings')) this.hideSettings();
        else this.showSettings();
    }

    private showSettings(): void {
        this.closeCenterModals();
        this.modals.open('settings');
        this.settingsPanel.show();
        this.applyUIState();
    }

    private hideSettings(): void {
        this.modals.close('settings');
        this.settingsPanel.hide();
        this.applyUIState();
        this.refreshUI();
    }

    private showWelcome(): void {
        this.closeCenterModals();
        this.modals.open('welcome');
        this.welcomeIsVortex = false;
        this.welcomePanel.show();
        this.applyUIState();
    }

    /**
     * Greet a first-time Vortex player: shown when a Vortex grid is opened and no
     * Vortex grid carries any progress yet. Naturally fires once — playing anything
     * (or editing the Hub) records progress that suppresses it thereafter.
     */
    private maybeShowVortexWelcome(): void {
        if (this.isCurrentGridVortex() && !this.persistence.hasSavedVortexProgress()) {
            this.showVortexWelcome();
        }
    }

    /** The Vortex counterpart of the welcome, greeting a first-time Vortex player. */
    private showVortexWelcome(): void {
        this.closeCenterModals();
        this.modals.open('welcome');
        this.welcomeIsVortex = true;
        this.welcomePanel.showVortex(this.buildVortexWelcomeContext());
        this.applyUIState();
    }

    private hideWelcome(): void {
        this.modals.close('welcome');
        this.welcomeIsVortex = false;
        this.welcomePanel.hide();
        this.applyUIState();
        this.refreshUI();
    }

    /**
     * Build the Vortex welcome's context from the opened grid: its preset tier, whether
     * that tier was earned by clearing challenges, and which larger tiers remain locked.
     */
    private buildVortexWelcomeContext(): VortexWelcomeContext {
        // Vortex preset tiers, smallest first; the Tutorial size is always open.
        const TIERS = [
            { size: 6, label: 'Tutorial', dims: '6×6' },
            { size: 8, label: 'Medium', dims: '8×8' },
            { size: 10, label: 'Standard', dims: '10×10' },
        ];
        const size = this.currentGrid?.config.size ?? VORTEX_TUTORIAL_SIZE;
        const current = TIERS.find(t => t.size === size) ?? TIERS[0];
        const earnedUnlock = size > VORTEX_TUTORIAL_SIZE;
        const lockedSizes = TIERS
            .filter(t => t.size > VORTEX_TUTORIAL_SIZE && t.size !== size
                && this.persistence.getChallengeClears(t.size) < VORTEX_UNLOCK_CHALLENGES)
            .map(t => ({ label: t.label, dims: t.dims }));
        return { sizeLabel: current.label, earnedUnlock, lockedSizes };
    }

    /** Play a candidate grid from the admin panel: add to the portfolio if absent, then switch to it. */
    private handleAdminPlay(gridData: string): void {
        const existing = this.persistence.getGridsSorted().find(g => g.gridData === gridData);
        let id = existing?.id ?? null;
        if (!id) {
            id = this.persistence.addGrid(gridData);
            if (!id) { this.showMessage('Grid limit reached — remove some grids first'); return; }
        }
        // Mark it so the grid selector flags it as a backlog candidate, not a plain custom grid.
        this.persistence.setFromBacklog(id);
        this.hideAdmin();
        this.handleGridSelected(id);
    }

    /**
     * Promote a grid from the portfolio into the authoring backlog (authoring instance
     * only). The reverse of Play: that pulls a candidate in to play it, this pushes a
     * grid you liked out to the backlog so it can be solved and scheduled.
     */
    private async handleAddCandidate(savedGrid: SavedGrid): Promise<void> {
        try {
            // Send the grid's authored game set along, so a curator who swapped a game in
            // (the whole point of the 13-game pool) keeps that choice through the pipeline.
            const r = await AdminApi.addCandidate(savedGrid.gridData, gameTypesToScope(gridGameSet(savedGrid)));
            this.persistence.setFromBacklog(savedGrid.id);
            this.showMessage(r.added ? `Added to backlog as ${r.grid_hash}` : `Already in the backlog as ${r.grid_hash}`);
            this.gridSelector.refresh();
        } catch (err: any) {
            this.showMessage(`Add as candidate failed: ${err.message}`);
        }
    }

    /**
     * Capture the curator's local play of a backlog grid as a Gyrodiot draft.
     * Finds the local copy by grid data (it was added via Play), then serializes
     * its won games into the partial solution_data format. Returns null if the
     * grid was never played locally or no game is won yet.
     */
    private captureGyrodiotPlay(gridData: string): { data: string; solved: number } | null {
        const local = this.persistence.getGridsSorted().find(g => g.gridData === gridData);
        if (!local) return null;
        return this.persistence.exportSolvedStates(local.id);
    }

    /**
     * How many games the curator has won on their local copy of a grid, or null
     * if the grid was never played locally. Drives the admin "capture-ready" hint.
     */
    private localPlayState(gridData: string): number | null {
        const local = this.persistence.getGridsSorted().find(g => g.gridData === gridData);
        if (!local) return null;
        return this.persistence.exportSolvedStates(local.id)?.solved ?? 0;
    }

    private setupButtons(): void {
        // The "Next game" button is rewritten into the status line on every win, so
        // the listener lives on the status line itself rather than on the button.
        this.statusEl.addEventListener('click', (e) => {
            const btn = (e.target as HTMLElement).closest('#next-game') as HTMLElement | null;
            const gameType = btn?.dataset.game as GameType | undefined;
            if (gameType) this.switchGame(gameType);
        });

        this.sidebar.grids.btn.addEventListener('click', () => this.handleGridsButton());
        this.sidebar.adminBtn.addEventListener('click', () => this.toggleAdmin());
        this.sidebar.adminBackBtn.addEventListener('click', () => this.hideAdmin());
        this.sidebar.resetBtn.addEventListener('click', () => this.resetGame());
        this.sidebar.autoStartBtn.addEventListener('click', () => this.handleAutoStart());
        this.sidebar.settings.btn.addEventListener('click', () => this.toggleSettings());
        this.sidebar.welcomeCloseBtn.addEventListener('click', () => this.hideWelcome());
        this.sidebar.leaderboard.btn.addEventListener('click', () => this.toggleLeaderboard());
        if (import.meta.env?.VITE_ADMIN) {
            this.sidebar.exportStateBtn.addEventListener('click', () => this.handleExportState());
            this.sidebar.exportDataBtn.addEventListener('click', () => this.handleExportData());
            this.sidebar.importDataBtn.addEventListener('click', () => this.handleImportData());
        }

        this.gameButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const gameType = btn.dataset.game as GameType;
                if (gameType && gameType !== this.currentGameType) {
                    if (this.modals.inModal && !this.modals.isOpen('leaderboard')) return;
                    if (this.tutorialEngine?.isActive()) {
                        if (this.activeTutorialTitle) return; // meta-tutorial — ignore game buttons
                        this.switchTutorial(gameType);
                    } else {
                        // In the scoring overlay, switching games keeps the overlay:
                        // hide it (restores this game's board), switch, re-open for the
                        // next game (no-op if it has no recorded win).
                        const reopenScoring = this.scoringViewActive;
                        if (reopenScoring) this.hideScoringView();
                        this.switchGame(gameType);
                        if (reopenScoring) this.showScoringView();
                    }
                }
            });
            // Compact (short-screen) layout: surface the hovered game's name + score
            // below the grid, since buttons no longer expand inline.
            btn.addEventListener('mouseenter', () => this.showGameHoverInfo(btn));
            btn.addEventListener('mouseleave', () => this.clearGameHoverInfo());
        });

        // Compact layout: the vortex buttons don't expand inline (see CSS); surface
        // their label in the hover line, like the game buttons. The scoring star is
        // excluded — it now expands inline in compact too, so its label shows there.
        document.querySelectorAll<HTMLElement>('.vortex-icon-btn:not(#scoring-rules-btn)').forEach(btn => {
            btn.addEventListener('mouseenter', () => {
                const label = btn.querySelector('.icon-btn-label')?.textContent?.trim()
                    || btn.getAttribute('aria-label') || '';
                this.gameHoverInfoEl.textContent = label;
            });
            btn.addEventListener('mouseleave', () => this.clearGameHoverInfo());
        });

        this.sidebar.restoreBestBtn.addEventListener('click', () => this.handleRestoreBest());
        this.sidebar.restoreBackupBtn.addEventListener('click', () => this.handleRestoreBackup());
        this.sidebar.help.btn.addEventListener('click', () => this.handleHowToPlay());
        this.sidebar.exitTutorialBtn.addEventListener('click', () => this.exitTutorial());
        this.sidebar.vortexHubBtn.addEventListener('click', () => this.toggleVortexHub());
        this.sidebar.vortexActivateBtn.addEventListener('click', () => this.toggleVortexActivate());
        this.sidebar.vortexDeleteBtn.addEventListener('click', () => this.handleDeleteGatesButton());
        this.sidebar.scoringRulesBtn.addEventListener('click', () => this.toggleScoringView());
        this.updateHelpButton();
    }

    private applyUIState(): void {
        const inTutorial = !!this.tutorialEngine?.isActive();
        const inModal = this.modals.inModal;
        const isGame = !inTutorial && !inModal;

        // Canvas
        this.canvas.style.display = inModal ? 'none' : '';

        // Vortex buttons are hidden in any modal (re-evaluated by updateVortexButtons)
        if (inModal) {
            this.sidebar.vortexHubBtn.classList.add('hidden');
            this.sidebar.vortexActivateBtn.classList.add('hidden');
            this.sidebar.vortexDeleteBtn.classList.add('hidden');
        }

        // Game selector (12 game buttons)
        this.gameSelectorEl.style.display = (inModal || inTutorial) ? 'none' : '';
        this.gameButtons.forEach(btn => { btn.disabled = false; });

        // Game info (title + score): visible in game, tutorial, and tutorial hub
        this.gameInfoEl.style.display = (this.inGridModal) ? 'none' : '';
        // Restore buttons: only in game
        this.restoreButtonsEl.style.display = isGame ? '' : 'none';
        // Hide the star + score row outside a live game (tutorials/modals); the
        // advance hint takes its place below the title during tutorials.
        this.scoreStarRowEl.classList.toggle('tutorial-hidden', !isGame);
        // Tutorial: show the advance hint below the title (where the score sits).
        this.tutorialHintEl.classList.toggle('show', inTutorial);

        // Title
        if (this.modals.isOpen('welcome')) {
            this.gameTitleEl.textContent = this.welcomeIsVortex ? 'Welcome to Vortex Mode' : 'Welcome to Grid Games';
        } else if (this.modals.isOpen('settings')) {
            this.gameTitleEl.textContent = 'Settings';
        } else if (this.modals.isOpen('leaderboard')) {
            this.gameTitleEl.textContent = 'Leaderboard';
        } else if (this.modals.isOpen('tutorialHub')) {
            this.gameTitleEl.textContent = 'How to Play';
        } else if (inTutorial) {
            this.gameTitleEl.textContent = this.activeTutorialTitle ?? getGameTitle(this.currentGameType) + ' Tutorial';
        } else if (isGame) {
            this.gameTitleEl.textContent = getGameTitle(this.currentGameType);
        }

        // Grids button: opens the selector in game; closes whichever grid modal
        // is open. Marked active while a grid modal is showing.
        this.sidebar.grids.btn.style.display = (isGame || this.inGridModal) ? '' : 'none';
        this.sidebar.grids.btn.classList.toggle('active', this.inGridModal);
        // Grids button doubles as the back control inside the grid modals:
        // creator → grids selector, selector → game.
        if (this.modals.isOpen('gridCreator') || this.modals.isOpen('gridDuplicator')) {
            this.sidebar.grids.back('Back to Grids');
        } else if (this.modals.isOpen('gridSelector')) {
            this.sidebar.grids.back('Back to Game');
        } else {
            this.sidebar.grids.reset('Grids');
        }
        this.sidebar.resetBtn.style.display = isGame ? '' : 'none';
        // Auto-start: hidden outside game mode; updateAutoStartButton() refines
        // visibility (Base Grid + empty board) and enabled state in refreshUI().
        if (!isGame) this.sidebar.autoStartBtn.style.display = 'none';
        // Export/import are admin-only tools, surfaced inside the grid selector.
        // Toggle the `hidden` class (backed by CSS) so they stay hidden even before
        // applyUIState first runs — style.display alone would leave them visible.
        const adminInSelector = this.modals.isOpen('gridSelector') && !!import.meta.env?.VITE_ADMIN;
        this.sidebar.exportStateBtn.classList.toggle('hidden', !adminInSelector);
        this.sidebar.exportDataBtn.classList.toggle('hidden', !adminInSelector);
        this.sidebar.importDataBtn.classList.toggle('hidden', !adminInSelector);

        // Right-aligned control buttons: help or exit-tutorial, never both
        this.sidebar.help.btn.style.display = (isGame || this.modals.isOpen('tutorialHub')) ? '' : 'none';
        if (this.modals.isOpen('tutorialHub')) {
            this.sidebar.help.back('Back to Game');
        } else {
            this.sidebar.help.reset('How to play');
        }
        this.sidebar.exitTutorialBtn.classList.toggle('hidden', !inTutorial);

        // Settings button: opens the settings view in game; acts as a back control
        // while the settings view is open. Hidden in other modals/tutorials.
        this.sidebar.settings.btn.style.display = (isGame || this.modals.isOpen('settings')) ? '' : 'none';
        this.sidebar.settings.btn.classList.toggle('active', this.modals.isOpen('settings'));
        if (this.modals.isOpen('settings')) {
            this.sidebar.settings.back('Back to Game');
        } else {
            this.sidebar.settings.reset('Settings');
        }

        this.updateLeaderboardButton();

        // Canvas shadow
        if (inTutorial) {
            this.canvas.classList.remove('shadow-teal', 'shadow-amber');
            this.canvas.classList.add('shadow-gray');
        } else {
            this.canvas.classList.remove('shadow-gray');
        }

        // Restore best button
        if (!isGame) {
            this.sidebar.restoreBestBtn.classList.add('hidden');
        }

        // Status
        if (inModal) {
            this.statusEl.classList.remove('win');
        }
        // Welcome / Settings / Leaderboard take over the game area: drop the status
        // header and the game-only Scoring rules button. Welcome also exposes a Close
        // control in the (otherwise empty) left sidebar.
        const inPanel = this.modals.isOpen('welcome') || this.modals.isOpen('settings') || this.modals.isOpen('leaderboard');
        document.body.classList.toggle('panel-mode', inPanel);
        this.sidebar.scoringRulesBtn.style.display = inPanel ? 'none' : '';
        this.sidebar.welcomeCloseBtn.classList.toggle('hidden', !this.modals.isOpen('welcome'));

        this.updateScoringButton();
    }

    /** @deprecated Use applyUIState() instead — kept for GridSelector/GridCreator modal text */
    private setModalStatusText(statusText: string): void {
        this.statusEl.textContent = statusText;
        this.statusEl.classList.remove('win');
        this.gameScoreEl.textContent = '';
    }

    private handleGridsButton(): void {
        // Toggle: while a grid modal is open, the button closes it back to the
        // game; otherwise it opens the grid selector.
        if (this.modals.isOpen('gridCreator')) {
            // "Back to Grids": step back to the selector rather than all the way out.
            this.modals.close('gridCreator');
            this.gridCreator.hide();
            this.showGridSelector();
        } else if (this.modals.isOpen('gridDuplicator')) {
            this.gridDuplicator.hide();
            this.onGridDuplicatorClosed();  // also steps back to the selector
        } else if (this.modals.isOpen('gridSelector')) {
            this.hideGridSelector();
        } else {
            if (this.modals.isOpen('tutorialHub')) { this.modals.close('tutorialHub'); this.tutorialHub.hide(); }
            this.showGridSelector();
        }
    }

    private handleCreateFromSelector(): void {
        // "Create new Grid" row inside the selector hands off to the creator
        if (!this.persistence.canAddGrid()) {
            this.showMessage('Grid limit reached — remove some grids first');
            return;
        }
        this.modals.close('gridSelector');
        this.gridSelector.hide();
        this.showGridCreator();
    }

    private showGridCreator(): void {
        this.modals.open('gridCreator');
        this.gridCreator.show();
        this.applyUIState();
        this.setModalStatusText('Select a configuration below');
    }

    private onGridCreatorClosed(): void {
        this.modals.close('gridCreator');
        this.applyUIState();
        this.refreshUI();
    }

    // ── Duplicate modal ───────────────────────────────────────────
    // Shares the center column with the selector/creator, so opening it closes those.

    /** Digit positions of a grid-data string in ascending value order, for the risk check. */
    private static gridDigits(gridData: string): { digits: Position[]; size: number } {
        const { config, positions } = deserializeGridData(gridData);
        const digits = [...positions].sort((a, b) => a.digit - b.digit).map(p => p.pos);
        return { digits, size: config.size };
    }

    /** Duplicate an existing grid: pick the copy's set, and whether solutions come along. */
    private showGridDuplicator(savedGrid: SavedGrid): void {
        if (!this.persistence.canAddGrid()) {
            this.showMessage('Grid limit reached — remove some grids first');
            return;
        }
        const { digits, size } = App.gridDigits(savedGrid.gridData);
        this.duplicateTarget = { kind: 'grid', gridId: savedGrid.id };
        this.openDuplicator({
            title: savedGrid.isImported ? 'Make native copy' : 'Duplicate grid',
            initialSet: gridGameSet(savedGrid),
            confirmLabel: savedGrid.isImported ? 'Make Copy' : 'Duplicate',
            digits,
            gridSize: size,
            sourceGridData: savedGrid.gridData,
            isVortex: savedGrid.gridConfig?.isVortex === true,
            canCarry: true,
        });
    }

    /** A pasted grid code: same modal, nothing to carry over, so no score toggle. */
    private showImportDuplicator(gridData: string, gridConfig: GridConfig, gameSet?: string): void {
        const { digits, size } = App.gridDigits(gridData);
        this.duplicateTarget = { kind: 'import', gridData, gridConfig };
        this.openDuplicator({
            title: 'Add grid',
            initialSet: gameSet ? scopeToGameTypes(gameSet) : CLASSIC_GAME_TYPES,
            confirmLabel: 'Add Grid',
            digits,
            gridSize: size,
            sourceGridData: gridData,
            isVortex: gridConfig.isVortex === true,
            canCarry: false,
        });
    }

    private openDuplicator(request: DuplicateRequest): void {
        this.closeCenterModals();
        this.modals.open('gridDuplicator');
        this.gridDuplicator.show(request);
        this.applyUIState();
        this.setModalStatusText('Choose the games for this grid');
    }

    private onGridDuplicatorClosed(): void {
        this.duplicateTarget = null;
        this.modals.close('gridDuplicator');
        this.applyUIState();
        this.refreshUI();
        this.showGridSelector();
    }

    /** Jump to an existing variant picked from the duplicate modal's layout list. */
    private handleDuplicateVariantSelected(gridId: string): void {
        this.duplicateTarget = null;
        this.modals.close('gridDuplicator');
        // The modal hid itself; restore canvas + sidebar before loading so
        // handleGridSelected's layout.resize() can measure for real.
        this.applyUIState();
        this.handleGridSelected(gridId);
        this.gridSelector.setCurrentGridId(gridId);
        this.layout.resize();
    }

    private handleDuplicateConfirmed(result: DuplicateResult): void {
        const target = this.duplicateTarget;
        this.duplicateTarget = null;
        this.modals.close('gridDuplicator');
        // The modal hid itself; bring the canvas and sidebar back before loading the
        // copy, so layout.resize() inside handleGridSelected can measure for real.
        this.applyUIState();
        if (!target) return;

        const gameSet = result.gameSet === CLASSIC_SCOPE ? undefined : result.gameSet;
        const newId = target.kind === 'grid'
            ? this.persistence.duplicateGrid(target.gridId, { gameSet, copyScores: result.copyScores })
            : this.persistence.addImportedGrid(target.gridData, {}, false, target.gridConfig, gameSet);

        if (!newId) {
            this.showMessage('Grid limit reached — remove some grids first');
            this.showGridSelector();
            return;
        }

        this.showMessage(target.kind === 'grid' ? 'Grid duplicated' : 'Grid imported');
        // duplicateGrid/addImportedGrid both make the copy current; switch to it so the
        // player lands on the grid they just made rather than the one they copied from.
        this.handleGridSelected(newId);
        this.gridSelector.setCurrentGridId(newId);
        this.layout.resize();
    }

    private handleGridCreated(grid: Grid, gameSet?: string): void {
        // Save to persistence (guarded by the grid budget)
        const newId = this.persistence.addGrid(grid.serialize(), grid.config, gameSet);
        if (!newId) {
            this.showMessage('Grid limit reached — remove some grids first');
            return;
        }
        this.currentGridId = newId;
        this.currentGrid = grid;

        // Restrict the sidebar to this grid's game set before building any instance
        this.applyGameSet();

        // Destroy current input handler
        if (this.gameInstance) {
            this.gameInstance.input.destroy();
        }

        // Reset vortex state
        this.resetVortexState();

        // Clear all cached game instances
        this.gameInstances.clear();

        // Create game instance
        this.createCurrentGameInstance();

        // Update UI
        this.refreshUI();

        // A freshly created Vortex grid is a player's first Vortex grid: greet them.
        this.maybeShowVortexWelcome();
    }

    private showGridSelector(): void {
        this.modals.open('gridSelector');
        this.gridSelector.setCurrentGridId(this.currentGridId);
        this.gridSelector.show();
        this.applyUIState();
        this.setModalStatusText('Manage your saved grids');
    }

    private hideGridSelector(): void {
        this.modals.close('gridSelector');
        this.gridSelector.hide();
        this.applyUIState();
        this.refreshUI();
    }

    private onGridSelectorClosed(): void {
        this.modals.close('gridSelector');
        this.applyUIState();
        this.refreshUI();
        // Grid may have changed (Base ↔ Vortex shifts the sidebar height); now that
        // the panel is closed and the canvas is measurable again, re-pick the mode.
        this.layout.resize();
    }

    private handleGridSelected(gridId: string): void {
        const savedGrid = this.persistence.getGrid(gridId);
        if (!savedGrid) return;

        // Set as current grid
        this.persistence.setCurrentGrid(gridId);
        this.currentGridId = gridId;
        this.currentGrid = Grid.deserialize(savedGrid.gridData);

        // Restrict the sidebar to this grid's game set before building any instance
        this.applyGameSet();

        // Destroy current input handler
        if (this.gameInstance) {
            this.gameInstance.input.destroy();
        }

        // Reset vortex state
        this.resetVortexState();

        // Clear all cached game instances (different grid = fresh start)
        this.gameInstances.clear();

        // Create game instance for current game type
        this.createCurrentGameInstance();

        // Load saved state if available, or best solution
        const gameState = this.persistence.getGameState(gridId, this.currentGameType);
        if (gameState?.serialized) {
            this.deserializeCurrentGame(gameState.serialized);
        } else if (gameState?.bestSerialized) {
            this.deserializeCurrentGame(gameState.bestSerialized);
        }

        // Update UI
        this.refreshUI();

        // Re-pick the view mode for the new grid (Base ↔ Vortex shifts the
        // sidebar height). No-ops when reached via the grid selector (still in a
        // modal here); onGridSelectorClosed handles that path once it closes.
        this.layout.resize();

        // First time opening a Vortex grid with nothing played: greet with the
        // Vortex welcome (its own variant of the intro panel).
        this.maybeShowVortexWelcome();
    }

    private handleRestoreBest(): void {
        if (!this.currentGridId) return;

        const best = this.persistence.getBestScore(this.currentGridId, this.currentGameType);
        if (!best) return;

        this.deserializeCurrentGame(best.serialized);
        this.refreshUI();
        this.saveCurrentGameState();
    }

    private updateRestoreBestButton(): void {
        // Hide outside game mode (tutorial, grid selector/creator, vortex hub)
        if (this.tutorialEngine?.isActive() || this.inGridModal || this.isHubActive) {
            this.sidebar.restoreBestBtn.classList.add('hidden');
            return;
        }

        if (!this.currentGridId) {
            this.sidebar.restoreBestBtn.classList.add('hidden');
            return;
        }

        const best = this.persistence.getBestScore(this.currentGridId, this.currentGameType);
        if (!best) {
            this.sidebar.restoreBestBtn.classList.add('hidden');
            return;
        }

        // On Vortex grids, hide if best state's gates are incompatible with current Hub
        if (this.isCurrentGridVortex() && this.vortexHub && this.currentGrid) {
            const compatible = isBestStateCompatible(
                best.serialized,
                this.vortexHub.getGatePairings(),
                this.vortexHub.getGates(),
                this.currentGrid.serializer,
            );
            if (!compatible) {
                this.sidebar.restoreBestBtn.classList.add('hidden');
                return;
            }
        }

        const isWon = this.gameInstance?.checkWin() ?? false;

        // Hide if current state matches the best state exactly — but only while that win
        // is actually on show. A best state always wins, so if this one does not, some
        // unserialized display choice is hiding it (Walls lets the player pin a start
        // direction), and restoring is the way back.
        const currentSerialized = this.serializeCurrentGame();
        if (currentSerialized === best.serialized && isWon) {
            this.sidebar.restoreBestBtn.classList.add('hidden');
            return;
        }

        // If current state is a win with equal-or-better score, no need to restore
        if (isWon) {
            const currentScore = this.gameInstance?.getScore();
            if (currentScore && currentScore.totalScore <= best.score.totalScore) {
                this.sidebar.restoreBestBtn.classList.add('hidden');
                return;
            }
        }

        this.sidebar.restoreBestBtn.classList.remove('hidden');
        this.sidebar.restoreBestBtn.innerHTML = `${UNDO_ICON}<span class="restore-label">Restore best</span><span class="restore-best-score">${best.score.totalScore}</span>`;
    }

    private updateRestoreBackupButton(): void {
        this.sidebar.restoreBackupBtn.classList.add('hidden');
        if (this.restoreBackupPending) {
            this.restoreBackupPending = false;
            this.sidebar.restoreBackupBtn.style.minHeight = '';
            this.sidebar.restoreBackupBtn.innerHTML = `${UNDO_ICON}<span class="restore-label">Restore backup</span>`;
            this.sidebar.restoreBackupBtn.classList.remove('confirm');
        }

        if (!this.currentGridId || !this.isCurrentGridVortex()) return;
        if (this.isHubActive || this.tutorialEngine?.isActive() || this.inGridModal) return;

        const backup = this.persistence.getVortexBackup(this.currentGridId);
        if (!backup) return;

        const grid = this.persistence.getGrid(this.currentGridId);
        if (!grid) return;

        if (this.vortexBackup.shouldOfferRestore(grid, backup)) {
            const { winCount, totalScore } = this.vortexBackup.summary(backup);
            this.sidebar.restoreBackupBtn.classList.remove('hidden');
            this.sidebar.restoreBackupBtn.innerHTML = `${UNDO_ICON}<span class="restore-label">Restore backup</span><span class="restore-backup-score">${winCount} wins, ${totalScore}</span>`;
        }
    }

    private handleRestoreBackup(): void {
        if (!this.currentGridId) return;

        if (!this.restoreBackupPending) {
            this.restoreBackupPending = true;
            // Lock the current height so swapping to the shorter "Sure?" label
            // (which drops the score sub-line) doesn't shrink the button.
            this.sidebar.restoreBackupBtn.style.minHeight = `${this.sidebar.restoreBackupBtn.offsetHeight}px`;
            this.sidebar.restoreBackupBtn.innerHTML = `${UNDO_ICON}<span class="restore-label">Sure?</span>`;
            this.sidebar.restoreBackupBtn.classList.add('confirm');
            return;
        }

        // Confirmed: restore backup
        this.restoreBackupPending = false;
        this.sidebar.restoreBackupBtn.style.minHeight = '';
        this.sidebar.restoreBackupBtn.innerHTML = `${UNDO_ICON}<span class="restore-label">Restore backup</span>`;
        this.sidebar.restoreBackupBtn.classList.remove('confirm');

        const backup = this.persistence.getVortexBackup(this.currentGridId);
        if (!backup) return;

        // 1. Restore Hub state
        if (this.vortexHub) {
            this.vortexHub.deserialize(backup.hubState);
        } else if (this.currentGrid) {
            this.vortexHub = new VortexHub(this.currentGrid);
            this.vortexHub.deserialize(backup.hubState);
        }
        const canonicalHub = this.vortexHub?.serialize() ?? backup.hubState;
        this.persistence.saveVortexGridData(this.currentGridId, canonicalHub);

        // Normalize backup so its hubState matches the canonical serialization
        if (canonicalHub !== backup.hubState) {
            backup.hubState = canonicalHub;
            this.persistence.saveVortexBackup(this.currentGridId, backup);
        }

        // 2. Restore won game states (non-won games untouched)
        for (const gameType of GAME_TYPES) {
            const backupWin = backup.winStates[gameType];
            if (!backupWin) continue;

            this.persistence.saveBestScoreForced(this.currentGridId, gameType, backupWin);
            this.persistence.saveGameState(this.currentGridId, gameType, backupWin);
        }

        // 3. Clear cached game instances (hub changed)
        this.gameInstances.clear();

        // 4. Reload current game
        this.createCurrentGameInstance();
        const gameState = this.persistence.getGameState(this.currentGridId, this.currentGameType);
        if (gameState?.serialized) {
            this.deserializeCurrentGame(gameState.serialized);
        }

        this.refreshUI();
    }

    private handleExportState(): void {
        if (this.isHubActive && this.vortexHub) {
            const state = this.vortexHub.serialize();
            if (!state) return;
            navigator.clipboard.writeText(state).then(() => {
                this.showMessage('Vortex Hub state copied');
            });
            return;
        }

        if (!this.gameInstance) return;
        const state = this.serializeCurrentGame();
        if (!state) return;
        navigator.clipboard.writeText(state).then(() => {
            this.showMessage(`${this.currentGameType} state copied`);
        });
    }

    /** Dev tool: copy the entire persisted blob to the clipboard. */
    private handleExportData(): void {
        const data = this.persistence.exportAll();
        navigator.clipboard.writeText(data).then(() => {
            this.showMessage('Persisted data copied to clipboard');
        });
    }

    /** Dev tool: replace persisted data from pasted JSON, then reload. */
    private handleImportData(): void {
        const json = window.prompt('Paste persisted data JSON to import (this replaces all saved data):');
        if (!json) return;
        if (this.persistence.importAll(json)) {
            window.location.reload();
        } else {
            this.showMessage('Invalid data — import aborted');
        }
    }

    private showMessage(message: string): void {
        this.copiedMessageEl.textContent = message;
        setTimeout(() => {
            this.copiedMessageEl.textContent = '';
        }, 3000);
    }

    private switchGame(gameType: GameType): void {
        // Deactivate Vortex Hub if active
        if (this.isHubActive) {
            this.deactivateVortexHub();
        }

        // Reset Activate Vortex mode when leaving a game
        if (this.vortexMode === VortexMode.IN_GAME_GATES) {
            this.vortexMode = VortexMode.NORMAL_GAME;
            if (this.vortexActivateInput) {
                this.vortexActivateInput.destroy();
                this.vortexActivateInput = null;
            }
            this.setCurrentGameGateMode(false);
        }

        // Update active button
        this.gameButtons.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.game === gameType);
        });

        this.currentGameType = gameType;
        this.gameTitleEl.textContent = getGameTitle(gameType);
        this.updateHelpButton();

        // Destroy current input handler
        if (this.gameInstance) {
            this.gameInstance.input.destroy();
        }

        // Try to get existing game instance, or create new one
        if (this.currentGrid) {
            const existingInstance = this.gameInstances.get(gameType);
            if (existingInstance) {
                // Reactivate existing instance with new input handler
                reactivateGameInstance(existingInstance, gameType, this.canvas, () => this.onGameUpdate());
                this.gameInstance = existingInstance;

                // Refresh Vortex data on Vortex grids
                if (this.isCurrentGridVortex()) {
                    this.ensureVortexHub();
                    this.updateCurrentGameVortexData();
                }
            } else {
                // Create new game instance
                this.createCurrentGameInstance();
                // Load saved state if available, or best solution
                if (this.currentGridId) {
                    const gameState = this.persistence.getGameState(this.currentGridId, gameType);
                    if (gameState?.serialized) {
                        this.deserializeCurrentGame(gameState.serialized);
                    } else if (gameState?.bestSerialized) {
                        this.deserializeCurrentGame(gameState.bestSerialized);
                    }
                }
            }
            this.refreshUI();
        }
    }

    private deserializeCurrentGame(serialized: string): void {
        if (!this.gameInstance) return;
        this.gameInstance.game.deserialize(serialized);
    }

    private serializeCurrentGame(): string {
        if (!this.gameInstance) return '';
        return this.gameInstance.game.serialize();
    }

    private loadSavedOrDefaultGrid(): void {
        const savedGrid = this.persistence.getCurrentGrid();
        if (savedGrid) {
            this.currentGridId = savedGrid.id;
            this.currentGrid = Grid.deserialize(savedGrid.gridData);

            // Restrict the sidebar to this grid's game set before building any instance
            this.applyGameSet();

            // Destroy current input handler if exists
            if (this.gameInstance) {
                this.gameInstance.input.destroy();
            }

            // Clear cached instances and create fresh
            this.gameInstances.clear();
            this.createCurrentGameInstance();

            // Load saved game state if exists, or best solution if available
            const gameState = this.persistence.getGameState(savedGrid.id, this.currentGameType);
            if (gameState?.serialized) {
                this.deserializeCurrentGame(gameState.serialized);
            } else if (gameState?.bestSerialized) {
                this.deserializeCurrentGame(gameState.bestSerialized);
            }

            this.refreshUI();
        } else {
            // No saved grid, start fresh with a random one
            this.startNewGame();
        }
    }

    private startNewGame(): void {
        // Generate new grid
        const newGrid = this.generator.generate();

        // Save it to persistence
        this.currentGridId = this.persistence.addGrid(newGrid.serialize());
        this.currentGrid = newGrid;

        // Restrict the sidebar to this grid's game set before building any instance
        this.applyGameSet();

        // Destroy current input handler
        if (this.gameInstance) {
            this.gameInstance.input.destroy();
        }

        // Clear all cached game instances (new grid = fresh start)
        this.gameInstances.clear();

        // Create game instance
        this.createCurrentGameInstance();

        // Update UI
        this.refreshUI();

        // Refresh grid selector if visible
        if (this.modals.isOpen('gridSelector')) {
            this.gridSelector.show();
        }
    }

    private createCurrentGameInstance(): void {
        if (!this.currentGrid) return;

        this.gameInstance = createGameInstance(
            this.currentGameType,
            this.currentGrid,
            this.canvas,
            () => this.onGameUpdate()
        );

        // Store the instance for later reuse
        this.gameInstances.set(this.currentGameType, this.gameInstance);

        // Initialize Vortex data on Vortex grids
        if (this.isCurrentGridVortex()) {
            this.ensureVortexHub();
            this.updateCurrentGameVortexData();
        }
    }

    private resetGame(): void {
        if (this.isHubActive && this.vortexHub) {
            if (!this.vortexResetPending) {
                this.vortexResetPending = true;
                this.sidebar.resetLabelEl.textContent = 'Sure?';
                this.sidebar.resetBtn.classList.add('confirm');
                return;
            }
            this.vortexResetPending = false;
            this.sidebar.resetLabelEl.textContent = 'Reset';
            this.sidebar.resetBtn.classList.remove('confirm');
            this.vortexHub.reset();
            this.onVortexHubUpdate();
            return;
        }
        if (this.gameInstance) {
            this.gameInstance.reset();
            // Clear saved state (but keep best score)
            if (this.currentGridId) {
                this.persistence.clearGameState(this.currentGridId, this.currentGameType);
            }
            // Back to a blank board: send the keyboard cursor back to the start too.
            this.keyboard.resetCursor();
            this.refreshUI();
        }
    }

    private handleAutoStart(): void {
        if (!this.gameInstance) return;
        const game = this.gameInstance.game;
        if (typeof game.autoStart !== 'function') return;
        if (this.isCurrentGridVortex() || game.isStarted()) return;

        if (game.autoStart()) {
            this.onGameUpdate();   // saves state + refreshUI()
        } else {
            this.showMessage('Sorry, auto-start failed');
        }
    }

    /**
     * Auto-start button: shown on every Base Grid in game mode. Enabled only
     * when the current game implements autoStart() and the board is empty;
     * disabled otherwise. Hidden in modals/tutorials/hub and on Vortex grids.
     */
    private updateAutoStartButton(): void {
        const inTutorial = !!this.tutorialEngine?.isActive();
        const inModal = this.modals.inModal;
        const isGame = !inTutorial && !inModal && !this.isHubActive;

        const visible = isGame && !this.isCurrentGridVortex();
        this.sidebar.autoStartBtn.style.display = visible ? '' : 'none';
        if (!visible) return;

        const supported = typeof this.gameInstance?.game.autoStart === 'function';
        const started = this.gameInstance?.game.isStarted() ?? false;
        this.sidebar.autoStartBtn.disabled = !supported || started;
    }

    private clearVortexResetConfirm(): void {
        if (this.vortexResetPending) {
            this.vortexResetPending = false;
            this.sidebar.resetLabelEl.textContent = 'Reset';
            this.sidebar.resetBtn.classList.remove('confirm');
        }
    }

    private onGameUpdate(): void {
        const earned = this.saveCurrentGameState();
        this.refreshUI();
        // After the refresh: the score button only becomes visible in there, and an
        // animation on a hidden button plays to nobody.
        this.playWinFlourish(earned);
    }

    private refreshUI(): void {
        this.updateVortexButtons();
        this.updateButtonStatuses();
        this.render();
        this.updateStatus();
        this.updateScoreDisplay();
        this.updateAutoStartButton();
        this.updateRestoreBestButton();
        this.updateRestoreBackupButton();
        this.updateHelpButton();
        this.updateLeaderboardButton();
    }

    /** Returns what the move earned, for {@link playWinFlourish}; null if nothing. */
    private saveCurrentGameState(): WinFlourish | null {
        if (!this.currentGridId || !this.gameInstance) return null;

        const serialized = this.serializeCurrentGame();
        const isWon = this.gameInstance.checkWin();
        const score = this.gameInstance.getScore();

        this.persistence.saveGameState(this.currentGridId, this.currentGameType, serialized);

        // If won, also update best score
        if (!isWon || !score) return null;

        // Read before saving: a first win is the one that makes the score button
        // appear, which is what the flourish is announcing.
        const hadBest = !!this.persistence.getBestScore(this.currentGridId, this.currentGameType);
        const improved = this.persistence.saveBestScore(
            this.currentGridId,
            this.currentGameType,
            serialized,
            score
        );

        // Update vortex backup if one exists and current state is better
        if (this.isCurrentGridVortex()) {
            this.vortexBackup.maybeUpdate();
        }

        // On a challenge grid, push to the leaderboard per the submit-mode
        // preference, and record full completion for the per-size Vortex gate.
        this.onChallengeWin();

        return {
            firstWin: !hadBest,
            improved,
            gridComplete: this.persistence.getTotalScore(this.currentGridId) !== null
        };
    }

    /**
     * Punctuate a win. The frame flashes the grid's own colour, and the score button
     * — which has just appeared — inverts as the frame lets go, so the two read as one
     * beat rather than two events. Improving a best that already existed inverts the
     * button on its own: the number under it changed and nothing else did.
     *
     * Winning again without beating the stored best does nothing. Nothing appeared
     * and nothing improved, so there is nothing to announce.
     *
     * The flash is skipped on the win that completes the grid: `updateCanvasShadow`
     * lights the frame permanently there, and a flash ending by fading back to the
     * panel colour would fight it. The lasting glow is the better flourish anyway.
     */
    private playWinFlourish(earned: WinFlourish | null): void {
        if (!earned?.improved) return;

        const flash = earned.firstWin && !earned.gridComplete;
        if (flash) {
            this.playAnimation(this.canvas, CANVAS_FLASH_CLASSES,
                this.isCurrentGridVortex() ? ['win-flash', 'win-flash-vortex'] : ['win-flash']);
        }
        this.playAnimation(this.sidebar.scoringRulesBtn, STAR_FLASH_CLASSES,
            flash ? ['star-flash', 'star-flash-delayed'] : ['star-flash']);
    }

    /**
     * Run a CSS animation by class, from the start. Re-adding a class the element
     * already carries does nothing on its own, so `all` comes off first — which also
     * clears whichever variant a previous run left behind — and a forced reflow
     * commits the removal before the class goes back on.
     */
    private playAnimation(el: HTMLElement, all: string[], on: string[]): void {
        el.classList.remove(...all);
        void el.offsetWidth;
        el.classList.add(...on);
        el.addEventListener('animationend', () => el.classList.remove(...all), { once: true });
    }

    // ── Challenges ────────────────────────────────────────────────

    /** The challenge linked to the current grid, or null if it isn't a challenge grid. */
    private currentChallengeRef(): { id: number; scope: string; size: number; endsAt: string } | null {
        if (!this.currentGridId) return null;
        const grid = this.persistence.getGrid(this.currentGridId);
        if (!grid || grid.challengeId == null) return null;
        return {
            id: grid.challengeId,
            scope: grid.challengeScope ?? '',
            size: grid.gridConfig?.size ?? 0,
            endsAt: grid.challengeEndsAt ?? '',
        };
    }

    /** Whether a challenge is still open for submissions (within its active window). */
    private challengeActive(endsAt: string): boolean {
        return !!endsAt && new Date(endsAt).getTime() > Date.now();
    }

    /** Whether every game in the challenge's scope is won on the current grid. */
    private isChallengeFullyWon(scope: string): boolean {
        if (!this.currentGridId || !scope) return false;
        return [...scope].every(initial => {
            const gameType = INITIAL_TO_GAME[initial];
            return !!gameType && !!this.persistence.getBestScore(this.currentGridId!, gameType);
        });
    }

    /** Handle a win on a challenge grid: submit per mode + record full completion. */
    private onChallengeWin(): void {
        const ref = this.currentChallengeRef();
        if (!ref) return;

        const full = this.isChallengeFullyWon(ref.scope);
        const mode = this.persistence.getSubmitMode();
        // Only auto-submit while the challenge is still open.
        if (this.challengeActive(ref.endsAt) && (mode === 'each' || (mode === 'full' && full))) {
            void this.submitCurrentChallenge();
        }
        // Local per-size Vortex gate: count the challenge once it's fully cleared,
        // independent of the submit mode (deduped by challenge id in persistence).
        if (full) this.persistence.recordChallengeCleared(ref.size, ref.id);
        this.updateLeaderboardButton(); // reveal the trophy + refresh amber state
    }

    /** Push the current grid's won scope games to the challenge leaderboard. */
    private async submitCurrentChallenge(): Promise<void> {
        const ref = this.currentChallengeRef();
        const gridId = this.currentGridId;
        if (!ref || !gridId || !this.challengeActive(ref.endsAt)) return;
        const block = this.persistence.exportSolvedStates(gridId);
        if (!block) return;
        const res = await ApiClient.submitSolution(ref.id, block.data);
        if (res?.accepted) {
            this.persistence.markChallengeSubmitted(gridId, block.data);
            this.updateLeaderboardButton(); // clear the amber "pending" state
        }
    }

    /** Not in a tutorial, modal, admin view, or the Vortex Hub — i.e. actively
     *  playing on the board. Also gates the keyboard controller (K + placement). */
    private isInGame(): boolean {
        const inTutorial = !!this.tutorialEngine?.isActive();
        const inModal = this.modals.inModal;
        return !inTutorial && !inModal && !this.adminVisible && !this.isHubActive;
    }

    /** Whether the keyboard may act. Wider than {@link isInGame}: the Vortex Hub has
     *  its own cell FSM for the cursor to drive, so the keyboard stays live there. */
    private isKeyboardActive(): boolean {
        return this.isInGame() || (this.isHubActive && !this.modals.inModal && !this.adminVisible);
    }

    /**
     * What the keyboard dispatches to. The Vortex Hub and Activate Vortex are
     * App-owned surfaces with their own cell FSMs, so while either is up it takes
     * over from the game's own input.
     */
    private activeKeyboardInput(): unknown {
        if (this.isHubActive) return this.vortexHubInput ?? null;
        if (this.vortexActivateInput) return this.vortexActivateInput;
        return this.gameInstance?.input ?? null;
    }

    /** `V`: the Activate Vortex button. Inert off a Vortex grid or inside the Hub. */
    private toggleGateModeFromKeyboard(): void {
        if (this.isHubActive || !this.isCurrentGridVortex()) return;
        this.toggleVortexActivate();
    }

    /** `H`: the Vortex Hub button. Inert off a Vortex grid. */
    private toggleHubFromKeyboard(): void {
        if (!this.isCurrentGridVortex()) return;
        this.toggleVortexHub();
    }

    /** Local wins on a challenge grid not yet reflected in an accepted submission. */
    private hasUnsubmittedChallengeWins(gridId: string): boolean {
        const block = this.persistence.exportSolvedStates(gridId);
        if (!block) return false;
        return this.persistence.getGrid(gridId)?.challengeSubmitted !== block.data;
    }

    /**
     * Trophy button: shown on a challenge grid with ≥1 game won (or while the
     * leaderboard is open); amber when an active challenge has unsubmitted wins.
     * Called from both applyUIState and refreshUI so a win reveals it immediately.
     */
    private updateLeaderboardButton(): void {
        const gridId = this.currentGridId;
        const ref = this.currentChallengeRef();
        const solved = gridId ? (this.persistence.exportSolvedStates(gridId)?.solved ?? 0) : 0;
        const onChallenge = this.isInGame() && !!ref && solved >= 1;

        this.sidebar.leaderboard.btn.classList.toggle('hidden', !(onChallenge || this.modals.isOpen('leaderboard')));
        this.sidebar.leaderboard.btn.classList.toggle('active', this.modals.isOpen('leaderboard'));
        const pending = onChallenge && !!ref && this.challengeActive(ref.endsAt)
            && this.hasUnsubmittedChallengeWins(gridId!);
        this.sidebar.leaderboard.btn.classList.toggle('pending', pending);

        if (this.modals.isOpen('leaderboard')) {
            this.sidebar.leaderboard.back('Back to Game');
        } else {
            this.sidebar.leaderboard.reset('Rankings');
        }
    }

    /**
     * Boot: fetch recent challenges (newest first), auto-add each to the portfolio as
     * a tagged grid (so the latest of each track pins as a challenge card and the rest
     * fill "Past Challenges" as full grid cards), and reconcile stale tags.
     * Best-effort/offline-safe.
     */
    private async initChallenges(): Promise<void> {
        const res = await ApiClient.getRecentChallenges();
        if (!res) return; // offline — leave challenge tags untouched

        // Endpoint returns newest-first, both tracks interleaved.
        const refs: ChallengeRef[] = res.challenges.map(c => ({
            id: c.id,
            title: c.title,
            gridData: c.grid_data,
            scope: c.game_scope,
            endsAt: c.ends_at,
            isVortex: c.is_vortex,
        }));

        // Authoritative: tag grids matching a recent challenge, clear tags that no
        // longer correspond to one (and never tag defaults). Self-heals stale tags.
        this.persistence.reconcileChallengeMetadata(refs);

        // Ensure each recent challenge's grid is in the portfolio (idempotent, no
        // switch) so they all render as full grid cards.
        for (const ref of refs) this.persistence.addChallengeGrid(ref);

        // One pinned card per track: Base and Vortex publish on their own weekdays and
        // cadences, so the newest overall is not the newest of each — taking refs[0]
        // alone would let a fresh Vortex hide the live Base challenge (or the reverse).
        // Vortex refs only reach a player who has unlocked the track; the server
        // filters them out otherwise, so the card simply doesn't appear.
        const latestBase = refs.find(r => !r.isVortex);
        const latestVortex = refs.find(r => r.isVortex);
        this.gridSelector.setCurrentChallenges(
            [latestBase, latestVortex].filter((r): r is ChallengeRef => !!r)
        );

        // The current grid may now be a tagged challenge → refresh button visibility.
        this.applyUIState();
    }

    /**
     * Enter a challenge from the selector's fallback minimal card (only used when the
     * challenge grid couldn't be auto-added): add it, close the selector, and play it,
     * mirroring how a normal grid row navigates.
     */
    private handleChallengeSelected(challenge: ChallengeRef): void {
        const id = this.persistence.addChallengeGrid(challenge);
        if (!id) { this.showMessage('Grid limit reached — remove some grids first'); return; }
        this.modals.close('gridSelector');
        this.gridSelector.hide();
        this.handleGridSelected(id);
        this.applyUIState();
    }

    /**
     * How far along a game is on the current grid, read from its live instance when
     * one exists and from saved state otherwise, plus the score to show under the
     * button (only ever set when complete). Single source of truth for the button
     * colours and for finding the next unsolved game.
     */
    private getGameProgress(
        gameType: GameType
    ): { status: 'not-started' | 'in-progress' | 'complete'; score: number | null } {
        const instance = this.gameInstances.get(gameType);
        if (instance) {
            const status = instance.getGameStatus();
            const score = status === 'complete' ? instance.getScore() : null;
            return { status, score: score ? score.totalScore : null };
        }

        if (!this.currentGridId) return { status: 'not-started', score: null };

        const best = this.persistence.getBestScore(this.currentGridId, gameType);
        // On Vortex grids, a best score is only valid if its gates still exist in the Hub
        const bestValid = best && (
            !this.vortexHub || !this.currentGrid
            || isBestStateCompatible(
                best.serialized,
                this.vortexHub.getGatePairings(),
                this.vortexHub.getGates(),
                this.currentGrid.serializer,
            )
        );
        if (bestValid) return { status: 'complete', score: best.score.totalScore };

        const gameState = this.persistence.getGameState(this.currentGridId, gameType);
        return { status: gameState?.serialized ? 'in-progress' : 'not-started', score: null };
    }

    /**
     * The next game in sidebar order with no win on this grid, wrapping past the
     * end. Null once every game is complete.
     */
    private getNextUnsolvedGame(): GameType | null {
        const set = this.currentGameSet();
        const order = orderedGameTypes(this.persistence.getGameOrder()).filter(g => set.has(g));
        const current = order.indexOf(this.currentGameType);

        for (let i = 1; i <= order.length; i++) {
            const gameType = order[(current + i) % order.length];
            if (gameType === this.currentGameType) continue;
            if (this.getGameProgress(gameType).status !== 'complete') return gameType;
        }

        return null;
    }

    private updateButtonStatuses(): void {
        this.gameButtons.forEach(btn => {
            const gameType = btn.dataset.game as GameType;
            if (!gameType) return;

            // Remove all status classes
            btn.classList.remove('status-not-started', 'status-in-progress', 'status-complete', 'vortex-pip');

            const scoreEl = btn.parentElement?.querySelector('.game-score') as HTMLElement | null;
            const { status, score } = this.getGameProgress(gameType);

            btn.classList.add(`status-${status}`);
            if (scoreEl) scoreEl.textContent = score !== null ? score.toString() : '';

            // Vortex pip: show if game has at least one active gate pair
            if (this.isCurrentGridVortex()) {
                const instance = this.gameInstances.get(gameType);
                let hasActiveGates = false;
                if (instance && isVortexGame(instance.game)) {
                    hasActiveGates = instance.game.getActiveGatePairs().size > 0;
                } else if (this.currentGridId) {
                    const gameState = this.persistence.getGameState(this.currentGridId, gameType);
                    if (gameState?.serialized) {
                        if (this.vortexHub && this.currentGrid) {
                            hasActiveGates = hasCompatibleGates(
                                gameState.serialized,
                                this.vortexHub.getGatePairings(),
                                this.vortexHub.getGates(),
                                this.currentGrid.serializer,
                            );
                        } else {
                            const pipeIdx = gameState.serialized.indexOf('|');
                            hasActiveGates = pipeIdx >= 0 && pipeIdx < gameState.serialized.length - 1;
                        }
                    }
                }
                if (hasActiveGates) btn.classList.add('vortex-pip');
            }
        });

        // Gate Manager: red borders on affected games during pending delete
        this.updateGateAffectedBorders();

        this.updateCanvasShadow();
        this.updateScoringButton();
    }

    /** Compact layout: show a game's name + score (read from its button) below the grid. */
    private showGameHoverInfo(btn: HTMLButtonElement): void {
        const gameType = btn.dataset.game as GameType;
        if (!gameType) return;
        const score = (btn.parentElement?.querySelector('.game-score') as HTMLElement | null)?.textContent?.trim();
        this.gameHoverInfoEl.textContent = score ? `${getGameTitle(gameType)} · ${score}` : getGameTitle(gameType);
    }

    /** Clear the hover line when nothing is hovered — the active game's name and
     *  score are already shown above the canvas. */
    private clearGameHoverInfo(): void {
        this.gameHoverInfoEl.textContent = '';
    }

    // ── Scoring rules overlay ──────────────────────────────────────

    private toggleScoringView(): void {
        if (this.scoringViewActive) this.hideScoringView();
        else this.showScoringView();
    }

    /**
     * The recorded best for the current game (what Restore best targets): the saved
     * win, or null if none / incompatible with the current Vortex Hub. Drives the
     * Scoring-rules button and overlay — independent of the live board state.
     */
    private currentGameBest() {
        if (!this.currentGridId) return null;
        const best = this.persistence.getBestScore(this.currentGridId, this.currentGameType);
        if (!best) return null;
        if (this.isCurrentGridVortex() && this.vortexHub && this.currentGrid) {
            const compatible = isBestStateCompatible(
                best.serialized,
                this.vortexHub.getGatePairings(),
                this.vortexHub.getGates(),
                this.currentGrid.serializer,
            );
            if (!compatible) return null;
        }
        return best;
    }

    /** Fade the board, highlight the scored elements of the recorded win, and show
     *  the rule in the status bar. The best is previewed read-only — the live board
     *  is restored on dismiss. */
    private showScoringView(): void {
        if (!this.gameInstance) return;
        const best = this.currentGameBest();
        if (!best) return;
        const game = this.gameInstance.game;

        // Preview the recorded win (read-only); remember the live state to restore.
        this.scoringPreviewRestore = null;
        if (this.serializeCurrentGame() !== best.serialized) {
            this.scoringPreviewRestore = game.serialize();
            game.deserialize(best.serialized);
        }

        this.scoringViewActive = true;
        this.sidebar.scoringRulesBtn.classList.add('active');

        // Static scene: stop renderer-driven tweens, or the next self-scheduled
        // frame repaints the board over the overlay (Orbits gate slots).
        this.gameInstance.renderer.setSelfAnimation?.(false);

        this.statusEl.classList.remove('win');
        this.statusEl.classList.add('scoring');
        this.statusEl.textContent = formatScoringRule(this.currentGameType);

        this.gameInstance.render();
        const highlight = game.getScoringHighlight?.() ?? {};
        this.gameInstance.renderer.drawScoringOverlay(game, highlight);

        // Any click on the canvas dismisses (capture phase, before the game input).
        this.canvas.addEventListener('click', this.dismissScoringView, true);
    }

    private dismissScoringView = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.hideScoringView();
    };

    private hideScoringView(): void {
        if (!this.scoringViewActive) return;
        this.scoringViewActive = false;
        this.canvas.removeEventListener('click', this.dismissScoringView, true);
        this.sidebar.scoringRulesBtn.classList.remove('active');
        this.statusEl.classList.remove('scoring');
        // Restore the live board if we swapped in the best for preview.
        if (this.scoringPreviewRestore !== null) {
            this.gameInstance?.game.deserialize(this.scoringPreviewRestore);
            this.scoringPreviewRestore = null;
        }
        // Re-enable tweening only after the live board is back on screen, so
        // undoing the preview snaps too instead of animating on dismiss.
        this.render();
        this.gameInstance?.renderer.setSelfAnimation?.(true);
        this.updateStatus();
    }

    /** Reveal the Scoring-rules button whenever a win is recorded for the current
     *  game (Restore-best target), in normal play. Invisible but space-reserving
     *  otherwise. Closes the overlay if it becomes invalid. */
    private updateScoringButton(): void {
        const available = !!this.currentGameBest()
            && !this.isHubActive && !this.tutorialEngine?.isActive()
            && !this.inGridModal && !this.modals.isOpen('tutorialHub');
        this.sidebar.scoringRulesBtn.classList.toggle('scoring-locked', !available);
        if (!available && this.scoringViewActive) this.hideScoringView();
    }

    private updateGateAffectedBorders(): void {
        const pending = this.isHubActive ? this.currentPendingDelete : null;

        this.gameButtons.forEach(btn => {
            const gameType = btn.dataset.game as GameType;
            if (!gameType) return;
            btn.classList.remove('gate-affected-win', 'gate-affected');
            if (!pending) return;

            const winsAffected = new Set(pending.usage.gamesUsingInBest);
            const allAffected = new Set([...pending.usage.gamesUsing, ...pending.usage.gamesUsingInBest]);
            if (winsAffected.has(gameType)) {
                btn.classList.add('gate-affected-win');
            } else if (allAffected.has(gameType)) {
                btn.classList.add('gate-affected');
            }
        });
    }

    private updateCanvasShadow(): void {
        this.canvas.classList.remove('shadow-teal', 'shadow-amber', 'shadow-purple');

        if (this.tutorialEngine?.isActive()) return;

        if (this.isHubActive) {
            this.canvas.classList.add('shadow-purple');
            return;
        }

        if (!this.currentGridId) return;

        const savedGrid = this.persistence.getGrid(this.currentGridId);
        if (!savedGrid) return;

        // Complete = all 12 games won (and, for Vortex, a valid Hub) — recomputed.
        // A finished Vortex grid glows purple like everything else Vortex. It used to
        // stay teal to keep the win apart from the Hub's own purple glow, but the
        // colour reads as "which kind of grid is this", not "which screen am I on".
        if (this.persistence.getTotalScore(this.currentGridId) !== null) {
            const win = this.isCurrentGridVortex() ? 'shadow-purple' : 'shadow-teal';
            this.canvas.classList.add(savedGrid.isImported ? 'shadow-amber' : win);
        }
    }

    private updateScoreDisplay(): void {
        if (this.isHubActive) {
            this.gameScoreEl.textContent = '';
            return;
        }

        if (!this.gameInstance) {
            this.gameScoreEl.textContent = '';
            return;
        }

        // Just the total now — the breakdown lives in the scoring overlay (star button).
        const score = this.gameInstance.getScore();
        this.gameScoreEl.textContent = score ? `${score.totalScore}` : '';
    }

    private render(): void {
        if (this.tutorialEngine?.isActive()) return;
        if (this.isHubActive) {
            this.renderVortexHub();
            return;
        }
        if (this.gameInstance) {
            this.applyRendererSettings();
            this.gameInstance.render();
        }
    }

    /** Push the user's per-game display preferences onto the active renderer.
     *  Cheap to repeat each frame; covers every game-switch path uniformly. */
    private applyRendererSettings(): void {
        const r = this.gameInstance?.renderer as unknown as {
            setHintBeamEnabled?: (b: boolean) => void;
            setHintRingsEnabled?: (b: boolean) => void;
            setHintsEnabled?: (b: boolean) => void;
            setKeyboardCursor?: (pos: Position | null, style?: KeyboardCursorStyle, hintFrom?: Position | null) => void;
            setKeyboardFineCursor?: (fine: FinePoint | null, style?: KeyboardCursorStyle, hintFrom?: FinePoint | null) => void;
            setKeyboardPointCursor?: (point: Position | null, style?: KeyboardCursorStyle) => void;
        } | undefined;
        if (!r) return;
        r.setHintBeamEnabled?.(this.persistence.getMirrorsHintBeam());
        r.setHintRingsEnabled?.(this.persistence.getBranchesHintRings());
        r.setHintsEnabled?.(this.persistence.getLineHints());
        // Keyboard cursor: pushed every frame so it survives self-animation
        // re-renders and follows game switches. Null when the mode is off. Only
        // one of the cell/fine/point cursors is non-null (per the active game's model).
        this.pushKeyboardCursor(r);
    }

    /**
     * Push the keyboard cursor onto a renderer. Done every frame so it survives
     * self-animation re-renders and follows game switches; null when the mode is off.
     * Only one of the cell/fine/point cursors is non-null (per the surface's model).
     * Shared by the game renderers and the Vortex Hub's, which draws it too.
     */
    private pushKeyboardCursor(r: {
        setKeyboardCursor?: (pos: Position | null, style?: KeyboardCursorStyle, hintFrom?: Position | null) => void;
        setKeyboardFineCursor?: (fine: FinePoint | null, style?: KeyboardCursorStyle, hintFrom?: FinePoint | null) => void;
        setKeyboardPointCursor?: (point: Position | null, style?: KeyboardCursorStyle) => void;
    }): void {
        const cursorStyle: KeyboardCursorStyle = this.keyboard.isDeleting()
            ? 'delete'
            : this.keyboard.isTracing() ? 'reticle' : 'crosshair';
        r.setKeyboardCursor?.(this.keyboard.getCursor(), cursorStyle, this.keyboard.getCursorTraceFrom());
        r.setKeyboardFineCursor?.(this.keyboard.getFineCursor(), cursorStyle, this.keyboard.getFineTraceFrom());
        r.setKeyboardPointCursor?.(this.keyboard.getPointCursor(), cursorStyle);
    }

    /** Every game on the current grid is cleared (best score present), ignoring
     *  overall Hub validity — the trigger for the grid-level endgame status. */
    private allGamesComplete(): boolean {
        if (!this.currentGridId) return false;
        const set = this.currentGameSet();
        return orderedGameTypes(this.persistence.getGameOrder())
            .filter(g => set.has(g))
            .every(g => this.getGameProgress(g).status === 'complete');
    }

    /**
     * Grid-level endgame status that overrides the per-game message once every game
     * is cleared: a teal (Base) / purple (Vortex) "Total score" banner flanked by
     * two score-stars, or — on a Vortex grid whose Hub is invalid — a red
     * "Invalid Hub". Returns false when the grid isn't finished, leaving the normal
     * per-game status untouched.
     */
    private renderEndgameStatus(): boolean {
        if (!this.allGamesComplete()) return false;

        this.statusEl.classList.remove('win', 'scoring');
        const isVortex = this.isCurrentGridVortex();

        // All games cleared but the Hub blocks the win: a hard red stop.
        if (isVortex && this.vortexHub && !this.vortexHub.validate().isValid) {
            this.statusEl.innerHTML =
                '<span class="status-text"><span style="color:var(--color-red);font-weight:bold">Invalid Hub</span>' +
                ' — all games are cleared but the Vortex Hub prevents your victory!</span>';
            return true;
        }

        const total = this.persistence.getTotalScore(this.currentGridId!);
        if (total === null) return false; // safety — shouldn't happen once complete

        const color = isVortex ? 'var(--color-purple)' : 'var(--color-teal)';
        const star = `<svg class="endgame-star" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${SCORE_STAR_PATH}"/></svg>`;
        const banner = (text: string) =>
            `<span class="status-text endgame-banner" style="color:${color}">${star}${text}${star}</span>`;

        // Full greeting when it fits; fall back to the bare score if it overflows
        // the header (measured after paint — mobile wraps instead, so it keeps full).
        this.statusEl.innerHTML = banner(`Congratulations! Total score: ${total}`);
        const header = this.statusEl.parentElement;
        if (header && this.statusEl.scrollWidth > header.clientWidth) {
            this.statusEl.innerHTML = banner(`Total score: ${total}`);
        }
        return true;
    }

    private updateStatus(): void {
        if (this.isHubActive) {
            this.updateVortexStatus();
            return;
        }

        if (this.vortexMode === VortexMode.IN_GAME_GATES && this.gameInstance && isVortexGame(this.gameInstance.game)) {
            const game = this.gameInstance.game;
            const pairs = game.getVortexGatePairs();
            const canActivateAny = pairs.some(([a]) => game.canActivateGate(a) || game.isActiveGate(a));
            this.statusEl.textContent = canActivateAny
                ? 'Click a pair to toggle it, or two empty cells to create a pair'
                : 'Click two empty cells to create a gate pair';
            this.statusEl.classList.remove('win');
            return;
        }

        if (this.gameInstance) {
            // Grid finished (all games cleared): a grid-level banner replaces the
            // per-game message, whichever game is on screen.
            if (this.renderEndgameStatus()) return;

            const status = this.gameInstance.getStatus();
            const won = this.gameInstance.checkWin();

            // The button lives inside the status line, so every other writer to
            // #status (tutorials, the scoring overlay, gate modes) drops it for free.
            // The message is wrapped so that #status, a flex row, holds one item per
            // element — its own runs of text must not become flex items of their own.
            const nextGame = won ? this.getNextUnsolvedGame() : null;
            this.statusEl.innerHTML = `<span class="status-text">${status}</span>` + (nextGame
                ? `<button id="next-game" data-game="${nextGame}">Next game →</button>`
                : '');

            this.statusEl.classList.toggle('win', won);
        }
    }

    // ── Vortex Hub ─────────────────────────────────────────────────

    private get isHubActive(): boolean {
        return this.vortexMode === VortexMode.HUB;
    }

    private isCurrentGridVortex(): boolean {
        if (!this.currentGridId) return false;
        const saved = this.persistence.getGrid(this.currentGridId);
        return saved?.gridConfig?.isVortex ?? false;
    }

    private updateVortexButtons(): void {
        const isVortex = this.isCurrentGridVortex();
        const inModal = this.modals.inModal;
        if (!isVortex || this.tutorialEngine?.isActive() || inModal) {
            this.sidebar.vortexHubBtn.classList.add('hidden');
            this.sidebar.vortexActivateBtn.classList.add('hidden');
            this.sidebar.vortexDeleteBtn.classList.add('hidden');
            return;
        }

        this.sidebar.vortexHubBtn.classList.remove('hidden');
        this.sidebar.vortexHubBtn.classList.toggle('active', this.isHubActive);
        // In the Hub the button leaves back to the interrupted game
        this.sidebar.vortexHubLabelEl.textContent = this.isHubActive
            ? `Back to ${getGameTitle(this.currentGameType)}`
            : 'Vortex Hub';

        if (this.isHubActive) {
            // Hub (Links mode): Vortex Hub (active) + Delete Gates; no Activate Vortex
            this.sidebar.vortexActivateBtn.classList.add('hidden');
            this.sidebar.vortexDeleteBtn.classList.remove('hidden');
            this.sidebar.vortexHubBtn.classList.remove('invalid');

            // Delete Gates: enabled whenever any gates exist (click to arm, or to
            // confirm a selected pair). Label reflects armed / confirming state.
            const hasGates = (this.vortexHub?.getGates().size ?? 0) > 0;
            this.sidebar.vortexDeleteBtn.disabled = !hasGates;
            const confirming = this.vortexDeletePendingInfo !== null;
            const armed = this.vortexDeleteArmed;
            this.sidebar.vortexDeleteBtn.classList.toggle('confirm', confirming);
            this.sidebar.vortexDeleteBtn.classList.toggle('active', armed && !confirming);
            this.sidebar.vortexDeleteLabelEl.textContent = confirming ? 'Sure?' : armed ? 'Pick a gate' : 'Delete Gates';
        } else {
            // Game mode: Vortex Hub + Activate Vortex
            this.sidebar.vortexDeleteBtn.classList.add('hidden');
            this.sidebar.vortexActivateBtn.classList.remove('hidden');
            this.sidebar.vortexActivateBtn.classList.toggle('active', this.vortexMode === VortexMode.IN_GAME_GATES);

            // Dashed red border when hub is invalid
            const hubInvalid = this.vortexHub ? !this.vortexHub.validate().isValid : false;
            this.sidebar.vortexHubBtn.classList.toggle('invalid', hubInvalid);

            // Dashed border on Activate Vortex when no pairs exist
            const noPairs = !this.vortexHub || this.vortexHub.getGatePairings().size === 0;
            this.sidebar.vortexActivateBtn.classList.toggle('dashed', noPairs);
        }
    }

    private toggleVortexHub(): void {
        this.canvas.style.opacity = '0';
        setTimeout(() => {
            if (this.isHubActive) {
                this.deactivateVortexHub();
            } else {
                this.activateVortexHub();
            }
            this.refreshUI();
            this.canvas.style.opacity = '1';
        }, 150);
    }

    private activateVortexHub(): void {
        if (!this.currentGrid || !this.currentGridId) return;

        // Save current game state first
        this.saveCurrentGameState();

        // Leave Activate Vortex mode if it was active
        if (this.vortexActivateInput) {
            this.vortexActivateInput.destroy();
            this.vortexActivateInput = null;
        }
        this.setCurrentGameGateMode(false);

        // Destroy game input
        if (this.gameInstance) {
            this.gameInstance.input.destroy();
        }

        this.vortexMode = VortexMode.HUB;
        this.vortexDeletePendingInfo = null;
        this.gameTitleEl.textContent = 'Vortex';

        // Create or reuse VortexHub
        if (!this.vortexHub) {
            this.vortexHub = new VortexHub(this.currentGrid);
            const saved = this.persistence.getVortexGridData(this.currentGridId);
            if (saved) {
                this.vortexHub.deserialize(saved);
            }
        }

        // Create renderer and input
        this.vortexHubRenderer = new VortexHubRenderer(this.canvas, this.currentGrid);
        this.vortexHubInput = new VortexHubInput(
            this.vortexHub,
            this.vortexHubRenderer,
            this.canvas,
            () => this.onVortexHubUpdate(),
            {
                getGatePairUsage: (gateKey) => this.lookupGatePairUsage(gateKey),
                onDeleteConfirmed: (pending) => this.handleDeleteConfirmed(pending),
                onDeleteArmedGatePicked: (pending) => this.handleDeleteArmedGatePicked(pending),
            }
        );

        // The Hub is a different board: park the game cursor and start from the Hub's
        // own origin. Leaving puts it back where it was.
        this.keyboard.stashCursor();
    }

    private deactivateVortexHub(): void {
        if (this.vortexHubInput) {
            this.vortexHubInput.destroy();
            this.vortexHubInput = null;
        }
        this.vortexHubRenderer = null;
        this.vortexMode = VortexMode.NORMAL_GAME;
        this.vortexDeletePendingInfo = null;
        this.vortexDeleteArmed = false;
        this.gameTitleEl.textContent = getGameTitle(this.currentGameType);
        this.clearVortexResetConfirm();

        // Clear gate mode highlights from game renderer/input
        this.setCurrentGameGateMode(false);

        // Reactivate game input
        if (this.currentGrid && this.gameInstance) {
            reactivateGameInstance(this.gameInstance, this.currentGameType, this.canvas, () => this.onGameUpdate());
        }

        // Push updated Vortex data to current game
        this.updateCurrentGameVortexData();

        // Back on the game board: return the cursor to where it was before the Hub.
        this.keyboard.restoreCursor();
    }

    /** Game mode: toggle "Activate Vortex" (in-game gate toggle/create) mode. */
    private toggleVortexActivate(): void {
        if (this.isHubActive || !this.isCurrentGridVortex()) return;
        const entering = this.vortexMode !== VortexMode.IN_GAME_GATES;
        this.vortexMode = entering ? VortexMode.IN_GAME_GATES : VortexMode.NORMAL_GAME;
        this.setActivateVortexMode(entering);
        this.updateVortexButtons();
        this.render();
        this.updateStatus();
    }

    /**
     * Delete Gates button (Hub Links mode). Three entry points:
     *  - already confirming → second click confirms the deletion.
     *  - a pair is selected (building) → enter delete-confirm for it.
     *  - nothing selected → toggle "armed" mode; the next gate click picks the
     *    pair to delete (see {@link handleDeleteArmedGatePicked}).
     */
    private handleDeleteGatesButton(): void {
        if (!this.isHubActive || !this.vortexHub) return;

        // Second click while confirming → delete.
        if (this.vortexDeletePendingInfo) {
            const pending = this.vortexDeletePendingInfo;
            this.vortexDeletePendingInfo = null;
            this.handleDeleteConfirmed(pending);
            return;
        }

        if (this.vortexHub.isBuilding()) {
            // A pair is selected → enter confirm state with usage warning.
            const startKey = this.vortexHub.getBuildStartGateKey();
            const partnerKey = this.vortexHub.getBuildPartnerKey();
            if (!startKey || !partnerKey) return;
            const usage = this.lookupGatePairUsage(startKey) ?? {
                gamesUsing: [], gamesUsingInBest: [], affectedCount: 0, invalidatedWins: 0,
            };
            this.enterDeleteConfirm({ clickedGateKey: startKey, pairedGateKey: partnerKey, usage });
            return;
        }

        // Nothing selected → toggle armed mode; next gate click picks the pair.
        this.vortexDeleteArmed = !this.vortexDeleteArmed;
        this.vortexHubInput?.setDeleteArmed(this.vortexDeleteArmed);
        this.refreshVortexHubUI();
    }

    /** Armed Delete Gates: a gate was clicked → move it into delete-confirm. */
    private handleDeleteArmedGatePicked(pending: PendingDelete): void {
        this.vortexDeleteArmed = false;
        this.enterDeleteConfirm(pending);
    }

    /** Show a pair as pending deletion ("Sure?") and refresh the Hub UI. */
    private enterDeleteConfirm(pending: PendingDelete): void {
        this.vortexDeletePendingInfo = pending;
        this.vortexHubInput?.setDeleteConfirm(pending);
        this.refreshVortexHubUI();
    }

    private refreshVortexHubUI(): void {
        this.updateVortexButtons();
        this.updateButtonStatuses();
        this.renderVortexHub();
        this.updateVortexStatus();
    }

    private onVortexHubUpdate(): void {
        if (this.currentGridId && this.vortexHub) {
            this.persistence.saveVortexGridData(this.currentGridId, this.vortexHub.serialize());
        }
        // Any canvas interaction dismisses the Delete Gates button confirm / armed state
        this.vortexDeletePendingInfo = null;
        this.vortexDeleteArmed = false;
        this.vortexHubInput?.setDeleteConfirm(null);
        this.vortexHubInput?.setDeleteArmed(false);
        this.clearVortexResetConfirm();
        this.updateVortexButtons();
        this.updateButtonStatuses();
        this.renderVortexHub();
        this.updateVortexStatus();
    }

    /** The pair pending deletion, from either the right-click confirm or the Delete Gates button. */
    private get currentPendingDelete(): PendingDelete | null {
        return this.vortexHubInput?.getPendingDelete() ?? this.vortexDeletePendingInfo ?? null;
    }

    private renderVortexHub(): void {
        if (this.vortexHubRenderer && this.vortexHub) {
            this.pushKeyboardCursor(this.vortexHubRenderer);
            this.vortexHubRenderer.render(this.vortexHub, false, {
                pendingDelete: this.currentPendingDelete ?? undefined,
                pendingCreate: this.vortexHubInput?.getPendingCreate() ?? undefined,
            });
        }
    }

    private updateVortexStatus(): void {
        if (!this.vortexHub) return;

        const pending = this.currentPendingDelete;
        if (pending) {
            let msg = `<b>Removing this gate will affect ${pending.usage.affectedCount} game${pending.usage.affectedCount !== 1 ? 's' : ''}</b>`;
            if (pending.usage.invalidatedWins > 0) {
                msg += `, <b>invalidating ${pending.usage.invalidatedWins} win state${pending.usage.invalidatedWins !== 1 ? 's' : ''}</b>`;
            }
            this.statusEl.innerHTML = msg;
            this.statusEl.classList.remove('win');
            return;
        }

        if (this.vortexHubInput?.getPendingCreate()) {
            this.statusEl.textContent = 'Click another empty cell to create the gate pair';
            this.statusEl.classList.remove('win');
            return;
        }

        if (this.vortexHub.isBuilding()) {
            this.statusEl.textContent = 'Click cells to trace the link to the paired gate';
            this.statusEl.classList.remove('win');
            return;
        }

        const v = this.vortexHub.validate();
        if (v.isValid) {
            this.statusEl.innerHTML =
                '<span style="color:var(--color-purple);font-weight:bold">Vortex Hub is valid.</span>';
            this.statusEl.classList.remove('win');
        } else {
            const issues: string[] = [];
            if (!v.allGatesLinked) issues.push('gates not all linked');
            if (!v.digitsConnected) issues.push('digits not all connected');
            this.statusEl.innerHTML =
                '<span style="color:#e94560;font-weight:bold">Hub invalid</span>: ' +
                issues.join(', ') + '.';
            this.statusEl.classList.remove('win');
        }
    }

    // ── Gate Manager ──────────────────────────────────────────────

    /** Look up gate pair usage for a given gate key. Returns null if unused. */
    private lookupGatePairUsage(gateKey: string): GatePairUsage | null {
        if (!this.currentGridId || !this.currentGrid || !this.vortexHub) return null;
        const grid = this.persistence.getGrid(this.currentGridId);
        if (!grid) return null;

        const pairings = this.vortexHub.getGatePairings();
        const pairedKey = pairings.get(gateKey);
        if (!pairedKey) return null;

        const gs = this.currentGrid.serializer;
        const serializedA = gs.serializePosition(parsePositionKey(gateKey));
        const serializedB = gs.serializePosition(parsePositionKey(pairedKey));

        const usage = getGatePairUsage(grid, serializedA, serializedB);
        return usage.affectedCount > 0 ? usage : null;
    }

    /** Handle confirmed deletion of a gate pair from the Hub. */
    private handleDeleteConfirmed(pending: PendingDelete): void {
        if (!this.currentGridId || !this.vortexHub) return;

        // Clear any in-progress build selection of this pair
        this.vortexHub.cancelBuild();

        // Create backup before removal if any win states are affected
        if (pending.usage.invalidatedWins > 0) {
            this.vortexBackup.maybeCreate();
        }

        // Remove the gate pair
        this.vortexHub.removeGatePair(pending.clickedGateKey);

        // Save hub state
        this.persistence.saveVortexGridData(this.currentGridId, this.vortexHub.serialize());

        // Clear cached game instances (they may reference removed gates)
        this.gameInstances.clear();

        // Refresh
        this.vortexDeletePendingInfo = null;
        this.vortexHubInput?.setDeleteConfirm(null);
        this.updateVortexButtons();
        this.updateButtonStatuses();
        this.renderVortexHub();
        this.updateVortexStatus();
    }

    private resetVortexState(): void {
        if (this.vortexHubInput) {
            this.vortexHubInput.destroy();
            this.vortexHubInput = null;
        }
        if (this.vortexActivateInput) {
            this.vortexActivateInput.destroy();
            this.vortexActivateInput = null;
        }
        this.vortexHubRenderer = null;
        this.vortexHub = null;
        this.vortexMode = VortexMode.NORMAL_GAME;
        this.vortexDeletePendingInfo = null;
    }

    // ── Vortex Game Integration ─────────────────────────────────────

    private ensureVortexHub(): void {
        if (this.vortexHub) return;
        if (!this.currentGrid || !this.currentGridId) return;

        this.vortexHub = new VortexHub(this.currentGrid);
        const saved = this.persistence.getVortexGridData(this.currentGridId);
        if (saved) {
            this.vortexHub.deserialize(saved);
        }
    }

    private updateCurrentGameVortexData(): void {
        if (!this.gameInstance || !this.vortexHub) return;
        if (isVortexGame(this.gameInstance.game)) {
            // Pass all pairs (linked + unlinked) so freshly created pairs appear
            // and are usable; tell the renderer which are linked (solid vs dashed).
            this.gameInstance.game.setVortexData(this.vortexHub.getAllGatePairs(), this.vortexHub.getGates());
            const r = this.gameInstance.renderer as unknown as { setLinkedGatePairIds?(ids: Set<string>): void };
            r.setLinkedGatePairIds?.(this.vortexHub.getLinkedPairIds());
        }
    }

    /**
     * Create a gate pair in the shared Hub from in-game Activate Vortex mode.
     * The new pair is paired-but-unlinked (Hub becomes invalid until linked) and
     * is auto-activated in the current game when both cells are free.
     * Returns false if the pair could not be created (e.g. a cell holds a digit).
     */
    private createGatePairInGame(a: Position, b: Position): boolean {
        this.ensureVortexHub();
        if (!this.vortexHub || !this.currentGridId) return false;
        if (!this.vortexHub.createPair(a, b)) return false;

        // Persist the Hub (other games refresh their gate data when reactivated)
        this.persistence.saveVortexGridData(this.currentGridId, this.vortexHub.serialize());

        // Push the new gate to the current game and auto-activate if possible
        this.updateCurrentGameVortexData();
        if (this.gameInstance && isVortexGame(this.gameInstance.game)) {
            const game = this.gameInstance.game;
            if (game.canActivateGate(a)) game.toggleVortexGate(a);
        }
        return true;
    }

    private setCurrentGameGateMode(enabled: boolean): void {
        if (!this.gameInstance) return;
        if (isGateModeReceiver(this.gameInstance.renderer)) this.gameInstance.renderer.setGateMode(enabled);
        if (isGateModeReceiver(this.gameInstance.input)) this.gameInstance.input.setGateMode(enabled);
    }

    /**
     * Enter/leave in-game "Activate Vortex" mode. Swaps the normal game input for
     * an App-owned controller that toggles gate pairs and creates new pairs.
     */
    private setActivateVortexMode(enabled: boolean): void {
        if (!this.gameInstance || !this.currentGrid) return;
        this.setCurrentGameGateMode(enabled);

        if (enabled) {
            if (!isVortexGame(this.gameInstance.game)) return;
            this.gameInstance.input.destroy();
            this.ensureVortexHub();
            this.vortexActivateInput = new VortexActivateInput(
                this.gameInstance.game,
                this.gameInstance.renderer as unknown as ActivateRenderer,
                this.canvas,
                () => this.onGameUpdate(),
                {
                    createPair: (a, b) => this.createGatePairInGame(a, b),
                    canPlaceGate: (pos) => this.vortexHub?.canPlaceGate(pos) ?? false,
                }
            );
        } else {
            if (this.vortexActivateInput) {
                this.vortexActivateInput.destroy();
                this.vortexActivateInput = null;
            }
            // Reactivate normal game input
            if (this.gameInstance) {
                reactivateGameInstance(this.gameInstance, this.currentGameType, this.canvas, () => this.onGameUpdate());
            }
        }
    }

    // ── Tutorial Hub ──────────────────────────────────────────────

    private handleHowToPlay(): void {
        if (this.modals.isOpen('tutorialHub')) {
            this.hideTutorialHub();
        } else {
            if (this.modals.isOpen('gridSelector')) { this.modals.close('gridSelector'); this.gridSelector.hide(); }
            if (this.modals.isOpen('gridCreator')) { this.modals.close('gridCreator'); this.gridCreator.hide(); }
            if (this.modals.isOpen('gridDuplicator')) { this.modals.close('gridDuplicator'); this.gridDuplicator.hide(); this.duplicateTarget = null; }
            this.showTutorialHub();
        }
    }

    private showTutorialHub(): void {
        this.modals.open('tutorialHub');
        this.tutorialHub.show(this.currentGameType, this.isCurrentGridVortex());
        this.applyUIState();
        this.setModalStatusText('Select a tutorial');
    }

    private hideTutorialHub(): void {
        this.modals.close('tutorialHub');
        this.tutorialHub.hide();
        this.applyUIState();
        this.refreshUI();
    }

    private onTutorialHubClosed(): void {
        this.modals.close('tutorialHub');
        this.applyUIState();
        if (!this.tutorialEngine?.isActive()) {
            this.refreshUI();
        }
    }

    private handleTutorialSelected(gameType: GameType, isVortex: boolean): void {
        // Switch to the selected game if needed
        if (gameType !== this.currentGameType) {
            this.gameButtons.forEach(btn => {
                btn.classList.toggle('active', btn.dataset.game === gameType);
            });
            this.currentGameType = gameType;
        }
        if (isVortex) {
            this.startVortexTutorial();
        } else {
            this.startTutorial();
        }
    }

    private handleMetaTutorialSelected(id: MetaTutorialId): void {
        const entry = getMetaTutorial(id);
        if (!entry) return;
        // Meta tutorials aren't tied to a game, so they carry their own heading.
        const title = id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        this.launchTutorial(entry, { isVortex: id === 'vortex-hub', title });
    }

    // ── Tutorial ───────────────────────────────────────────────────

    /**
     * Render scene-progress as a row of dots in the status header (above the grid,
     * where the old "N / total" text sat): past scenes filled-faint, the current
     * one a larger teal dot, upcoming ones hollow.
     */
    private renderTutorialProgress(index: number, total: number): void {
        this.statusEl.classList.remove('win');
        this.statusEl.textContent = '';
        const wrap = document.createElement('span');
        wrap.className = 'tut-progress' + (this.tutorialIsVortex ? ' vortex' : '');
        for (let i = 0; i < total; i++) {
            const dot = document.createElement('span');
            dot.className = 'tut-dot' + (i === index ? ' tut-dot-current' : i < index ? ' tut-dot-done' : '');
            wrap.appendChild(dot);
        }
        this.statusEl.appendChild(wrap);
    }

    private updateHelpButton(): void {
        const hasWin = !!(this.currentGridId && this.persistence.getBestScore(this.currentGridId, this.currentGameType));
        this.sidebar.help.btn.classList.toggle('has-win', hasWin);
    }

    /**
     * Start a tutorial: swap the live game out for the tutorial's own grid and
     * adapter, then run the engine. Shared by the game, Vortex and meta flavours —
     * they differ only in which registry the entry came from, whether the progress
     * dots are Vortex-themed, and (meta only) an explicit title.
     */
    private launchTutorial(entry: TutorialEntry, opts: { isVortex: boolean; title?: string }): void {
        const { definition, createAdapter } = entry;
        this.tutorialIsVortex = opts.isVortex;

        // Remember current grid so we can restore it on tutorial exit
        this.preTutorialGridId = this.currentGridId;
        this.activeTutorialTitle = opts.title ?? null;

        // Destroy current game input so it doesn't conflict
        if (this.gameInstance) {
            this.gameInstance.input.destroy();
        }

        const tutorialGrid = Grid.deserialize(definition.gridCode);
        const adapter = createAdapter(this.canvas, tutorialGrid);

        this.tutorialEngine = new TutorialEngine(
            definition,
            this.canvas,
            tutorialGrid,
            adapter,
            {
                onExit: () => this.exitTutorial(),
                onSceneChange: (index, total) => {
                    this.renderTutorialProgress(index, total);
                },
            }
        );

        this.enterTutorialMode();
        this.tutorialEngine.start();
    }

    private startTutorial(): void {
        const entry = getTutorial(this.currentGameType);
        if (entry) this.launchTutorial(entry, { isVortex: false });
    }

    private startVortexTutorial(): void {
        const entry = getVortexTutorial(this.currentGameType);
        if (entry) this.launchTutorial(entry, { isVortex: true });
    }

    private switchTutorial(gameType: GameType): void {
        // Stop the current tutorial
        if (this.tutorialEngine) {
            this.tutorialEngine.stop();
            this.tutorialEngine = null;
        }

        // Update active button and game type
        this.gameButtons.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.game === gameType);
        });
        this.currentGameType = gameType;

        // Start the new tutorial
        this.startTutorial();
    }

    private exitTutorial(): void {
        if (this.tutorialEngine) {
            this.tutorialEngine.stop();
            this.tutorialEngine = null;
        }

        // Clean up vortex sidebar buttons (re-evaluated by updateVortexButtons)
        this.sidebar.vortexActivateBtn.classList.add('hidden');
        this.sidebar.vortexDeleteBtn.classList.add('hidden');

        // Restore the grid (but don't fully exit tutorial mode UI yet)
        const gridToRestore = this.preTutorialGridId;
        this.preTutorialGridId = null;
        this.activeTutorialTitle = null;

        this.exitTutorialMode();

        if (gridToRestore) {
            this.handleGridSelected(gridToRestore);
        } else {
            this.loadSavedOrDefaultGrid();
        }

        // Return to the Tutorial Hub
        this.showTutorialHub();
    }

    private enterTutorialMode(): void {
        this.applyUIState();
        this.statusEl.classList.remove('win');
        this.statusEl.textContent = '';
    }

    private exitTutorialMode(): void {
        this.applyUIState();
    }

}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new App();
});
